import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/Button";
import { StepNavigator } from "../components/StepNavigator";
import { Card } from "../components/Card";
import { ConfirmModal } from "../components/ConfirmModal";
import { authFetch } from "../lib/authFetch";
import { useAppStore } from "../store/appStore";

interface FeedbackRecord {
  text: string;
  provided_by: string;
  created_at: string;
}

interface RFPOutput {
  step_number: number;
  agent_slug: string;
  tab_name: string;
  output: Record<string, unknown>;
  created_at: string;
  feedback?: FeedbackRecord[];
  agent_run_id?: number | null;
  accepted_output_run_id?: number | null;
  run_input_status?: string | null;
  run_status?: string | null;
}

const HOLD_RUN_STATUSES = new Set(["human_input_required", "missing_data", "question", "error"]);
const ACTIVE_JOB_STATUSES = new Set(["queued", "running", "retry_scheduled"]);

const parseJsonObject = (value: unknown): Record<string, unknown> | null => {
  if (typeof value !== "string") return null;
  try {
    const parsed = JSON.parse(value);
    return parsed && typeof parsed === "object" && !Array.isArray(parsed)
      ? (parsed as Record<string, unknown>)
      : null;
  } catch {
    return null;
  }
};

const unwrapRuntimePayload = (payload: Record<string, unknown>): Record<string, unknown> => {
  let current = payload;
  for (let i = 0; i < 3; i += 1) {
    const nestedContent = current.content;
    if (typeof nestedContent === "string") {
      const parsed = parseJsonObject(nestedContent);
      if (parsed) {
        current = parsed;
        continue;
      }
    }
    break;
  }
  return current;
};

const normalizeStepOutput = (output: RFPOutput | undefined): RFPOutput | undefined => {
  if (!output) return output;
  const raw = output.output ?? {};
  const normalized = unwrapRuntimePayload(raw);
  if (normalized !== raw) {
    return { ...output, output: normalized };
  }

  const rawOutputText = raw.raw_output_text;
  const parsedFromText = parseJsonObject(rawOutputText);
  if (parsedFromText) {
    // The text copy only fills gaps. It is the agent's original reply, so letting it
    // win would undo human changes to the stored fields - a reordered or edited
    // project pool would snap back to the agent's version on every render.
    const unwrapped = unwrapRuntimePayload(parsedFromText);
    const merged: Record<string, unknown> = { ...raw };
    for (const [key, value] of Object.entries(unwrapped)) {
      const stored = merged[key];
      const storedIsEmpty =
        stored === undefined ||
        stored === null ||
        (Array.isArray(stored) && stored.length === 0) ||
        (typeof stored === "string" && stored.trim() === "");
      if (storedIsEmpty) {
        merged[key] = value;
      }
    }
    return { ...output, output: merged };
  }

  return output;
};

interface RFPPipeline {
  current_step: number;
  total_steps: number;
  status: string;
}

interface PipelineStep {
  step: number;
  name: string;
  agent_slug: string;
  role?: string;
  description?: string;
}

// Which step builds the human-reviewed project pool and which one is final. The
// backend tags each pipeline step with a role; the Catalant numbers are the fallback
// for payloads that predate the role field.
const getReviewSteps = (steps: PipelineStep[] | undefined): { projectStep: number; finalStep: number } => {
  const projectStep = steps?.find((step) => step.role === "projects")?.step ?? 4;
  const finalStep = steps?.find((step) => step.role === "final")?.step ?? 5;
  return { projectStep, finalStep };
};

interface TimelineEvent {
  event_type: "agent_input" | "agent_output" | "human_feedback" | "accepted_output" | "memory_retrieval" | "tool_call";
  actor: string;
  step_number: number;
  timestamp: string;
  title: string;
  content: unknown;
  raw_content?: unknown;
  metadata?: Record<string, unknown>;
}

interface StepTimeline {
  events: TimelineEvent[];
  accepted_output: TimelineEvent | null;
  first_output_run_id: number | null;
  accepted_output_run_id: number | null;
}

interface StepVersionStats {
  outputVersion: number;
  latestOutputRunId: number | null;
  latestOutputAtMs: number | null;
  feedbackCount: number;
  latestFeedbackAtMs: number | null;
  feedbackPendingRerun: boolean;
}

interface RFPDetail {
  id: number;
  rfp_code?: string;
  title?: string;
  platform_type: string;
  status: string;
  input_text: string;
  bidder_notes?: string;
  bidder_notes_updated_at?: string | null;
  pipeline: RFPPipeline;
  pipeline_steps: PipelineStep[];
  outputs: RFPOutput[];
  timeline_by_step?: Record<string, StepTimeline>;
  created_at: string;
  active_jobs?: ActiveJob[];
}

interface ActiveJob {
  job_id: number;
  job_type: string;
  status: string;
  step_number: number | null;
  input_status?: string;
}

type StepUiState =
  | "completed"
  | "running"
  | "awaiting_input"
  | "current_head"
  | "blocked"
  | "not_run"
  | "rerun_required";

interface TargetedFeedbackSentence {
  section_key: string;
  section_label: string;
  sentence_index: number;
  sentence_text: string;
}

interface Step5ReviewSentence extends TargetedFeedbackSentence {
  id: string;
  global_index: number;
}

interface Step5ReviewSection {
  section_key: string;
  section_label: string;
  sentences: Step5ReviewSentence[];
}

interface Step5ReviewModel {
  fullText: string;
  sections: Step5ReviewSection[];
  sentences: Step5ReviewSentence[];
}

interface Step5DraftRange {
  section_key: string;
  section_label: string;
  start_sentence_index: number;
  end_sentence_index: number;
  selected_sentences: TargetedFeedbackSentence[];
  sentence_ids: string[];
  global_start: number;
  global_end: number;
  excerpt_text: string;
}

interface Step5Annotation extends Step5DraftRange {
  annotation_id: string;
  feedback_text: string;
}

interface Step4ExpertSelectionOptions {
  selectedExpertIds: string[];
  onToggleExpert: (expertId: string) => void;
  onResetExperts: () => void;
  searchText: string;
  onSearchTextChange: (value: string) => void;
  disabled?: boolean;
  // Selected-expert order is the order the pitch introduces them in.
  onMoveExpert: (expertId: string, direction: -1 | 1) => void;
  onReorderExperts: (sourceId: string, targetId: string) => void;
  draggingExpertId: string | null;
  dragOverExpertId: string | null;
  onDragStartExpert: (expertId: string) => void;
  onDragOverExpert: (expertId: string) => void;
  onDragEndExpert: () => void;
}

const formatContent = (value: unknown): string => {
  if (typeof value === "string") {
    return value;
  }
  return JSON.stringify(value ?? {}, null, 2);
};

const THREE_PART_PITCH_AGENT_SLUGS = new Set([
  "catalant-standard-pitch",
  "catalant-team-pitch",
  "umbrex-proposal",
  "umbrex-team-proposal",
]);

const STEP5_PITCH_AGENT_SLUGS = new Set([
  ...THREE_PART_PITCH_AGENT_SLUGS,
  "catalant-idb-pitch",
  "catalant-idb-team-pitch",
]);

const PROJECT_SELECTOR_AGENT_SLUGS = new Set(["catalant-project-selector", "umbrex-project-selector"]);

const isUmbrexSlug = (slug: string | undefined): boolean => Boolean(slug && slug.startsWith("umbrex-"));

const STANDARD_PITCH_PART_DEFS: Array<{ key: string; fallback: string }> = [
  { key: "part_1", fallback: "Background & Relevant Experience" },
  { key: "part_2", fallback: "Relevant / Similar Project Experience" },
  { key: "part_3", fallback: "Proposed Approach & Conclusion" },
];

const isPitchOutput = (output: RFPOutput | undefined): boolean =>
  Boolean(output && STEP5_PITCH_AGENT_SLUGS.has(output.agent_slug));

const splitIntoParagraphs = (text: string): string[] => {
  const normalizedText = text.replace(/\r/g, "").trim();
  if (!normalizedText) return [];

  const lines = normalizedText.split("\n");
  const paragraphs: string[] = [];
  let current: string[] = [];
  const numberedLine = /^\d+[.)]\s+/;

  const flush = () => {
    if (current.length === 0) return;
    const merged = current.join("\n").trim();
    if (merged) paragraphs.push(merged);
    current = [];
  };

  lines.forEach((line) => {
    const trimmed = line.trim();
    if (!trimmed) {
      flush();
      return;
    }
    if (numberedLine.test(trimmed) && current.length > 0) {
      flush();
    }
    current.push(trimmed);
  });
  flush();

  return paragraphs;
};

const getStandardPitchCombinedText = (output: Record<string, unknown>) => {
  const partContents: string[] = [];
  let computedCharacterCount = 0;

  STANDARD_PITCH_PART_DEFS.forEach((part) => {
    const section = (output[part.key] as Record<string, unknown> | undefined) ?? {};
    const content = String(section.content || "").trim();
    if (!content) return;
    partContents.push(content);
    computedCharacterCount += Number(section.character_count || content.length);
  });

  const combinedText = partContents.join("\n\n").trim();
  const explicitTotal = Number(output.total_character_count || 0);
  const totalCharacterCount =
    Number.isFinite(explicitTotal) && explicitTotal > 0 ? explicitTotal : computedCharacterCount;

  return {
    combinedText,
    totalCharacterCount,
  };
};

const buildStep5ReviewModel = (output: RFPOutput | undefined): Step5ReviewModel => {
  if (!output || !isPitchOutput(output)) {
    return { fullText: "", sections: [], sentences: [] };
  }

  const sections: Array<{ sectionKey: string; sectionLabel: string; text: string }> = [];
  if (THREE_PART_PITCH_AGENT_SLUGS.has(output.agent_slug)) {
    const { combinedText } = getStandardPitchCombinedText(output.output);
    if (combinedText) {
      sections.push({
        sectionKey: "final_pitch_text",
        sectionLabel: isUmbrexSlug(output.agent_slug) ? "Final Proposal" : "Final Pitch",
        text: combinedText,
      });
    }
  } else {
    const finalPitchText = String(output.output.final_pitch_text || "").trim();
    if (finalPitchText) {
      sections.push({ sectionKey: "final_pitch_text", sectionLabel: "Final Pitch", text: finalPitchText });
    }
    if (!finalPitchText) {
      const intro = (output.output.intro as Record<string, unknown> | undefined) ?? {};
      const introText = String(intro.content || "").trim();
      if (introText) {
        sections.push({ sectionKey: "intro", sectionLabel: "Intro", text: introText });
      }
      const responses = Array.isArray(output.output.responses)
        ? (output.output.responses as Array<Record<string, unknown>>)
        : [];
      responses.forEach((row, index) => {
        const itemType = String(row.item_type || "item").trim().toLowerCase();
        const itemNumber = Number(row.item_number || index + 1);
        const responseText = String(row.response || "").trim();
        if (!responseText) return;
        const sectionLabel = `${itemType === "question" ? "Question" : "Bullet"} ${itemNumber}`;
        sections.push({
          sectionKey: `responses.${index + 1}`,
          sectionLabel,
          text: responseText,
        });
      });
    }
  }

  const sentences: Step5ReviewSentence[] = [];
  const sectionBlocks: Step5ReviewSection[] = [];
  let globalIndex = 0;
  sections.forEach((section) => {
    const sectionSentences: Step5ReviewSentence[] = [];
    splitIntoParagraphs(section.text).forEach((sentence, sentenceIndex) => {
      const normalizedIndex = sentenceIndex + 1;
      globalIndex += 1;
      const entry = {
        id: `${section.sectionKey}:${normalizedIndex}`,
        section_key: section.sectionKey,
        section_label: section.sectionLabel,
        sentence_index: normalizedIndex,
        sentence_text: sentence,
        global_index: globalIndex,
      };
      sentences.push(entry);
      sectionSentences.push(entry);
    });
    if (sectionSentences.length > 0) {
      sectionBlocks.push({
        section_key: section.sectionKey,
        section_label: section.sectionLabel,
        sentences: sectionSentences,
      });
    }
  });

  const fullText = sections.map((section) => `${section.sectionLabel}\n${section.text}`).join("\n\n");
  return { fullText, sections: sectionBlocks, sentences };
};

const overlapsExistingAnnotation = (
  annotations: Step5Annotation[],
  candidateSentenceIds: string[],
  excludingAnnotationId?: string
): boolean => {
  const selectedSet = new Set(candidateSentenceIds);
  return annotations.some((annotation) => {
    if (excludingAnnotationId && annotation.annotation_id === excludingAnnotationId) {
      return false;
    }
    return annotation.sentence_ids.some((sentenceId) => selectedSet.has(sentenceId));
  });
};

const renderBulletList = (items: unknown, emptyLabel: string) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-slate-500">{emptyLabel}</p>;
  }

  return (
    <div className="space-y-2">
      {items.map((item, index) => (
        <div key={`${String(item)}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-sm text-slate-800">
          {String(item)}
        </div>
      ))}
    </div>
  );
};

interface ManualProjectPanelOptions {
  title: string;
  text: string;
  source: "jyc" | "expert";
  submitting: boolean;
  removingProjectId: string | null;
  disabled?: boolean;
  onTitleChange: (value: string) => void;
  onTextChange: (value: string) => void;
  onSourceChange: (value: "jyc" | "expert") => void;
  onAdd: () => void;
  onRemove: (projectId: string, isManual: boolean, title: string) => void;
  editingProjectId: string | null;
  editTitle: string;
  editText: string;
  savingEdit: boolean;
  onStartEdit: (projectId: string, title: string, text: string) => void;
  onEditTitleChange: (value: string) => void;
  onEditTextChange: (value: string) => void;
  onCancelEdit: () => void;
  onSaveEdit: () => void;
  // Pool order is the order the pitch uses, so reviewers drag the cards.
  reordering: boolean;
  orderSavedAt: number | null;
  armedProjectId: string | null;
  draggingProjectId: string | null;
  dragOverProjectId: string | null;
  onArmDrag: (projectId: string | null) => void;
  onDragStartProject: (projectId: string) => void;
  onDragOverProject: (projectId: string) => void;
  onDropProject: (projectId: string) => void;
  onDragEndProject: () => void;
  onMoveProject: (projectId: string, direction: -1 | 1) => void;
}

interface PitchEditOptions {
  editing: boolean;
  drafts: Record<string, string>;
  saving: boolean;
  disabled: boolean;
  partLimits: Record<string, number>;
  totalLimit: number | null;
  onStart: () => void;
  onChange: (partKey: string, value: string) => void;
  onCancel: () => void;
  onSave: () => void;
}

const UMBREX_PART_LIMITS: Record<string, number> = { part_1: 750, part_2: 3000, part_3: 750 };

interface PitchSectionOptions {
  hasBidderNotes: boolean;
}
const CATALANT_PITCH_TOTAL_LIMIT = 2500;

const renderProjectCards = (items: unknown, emptyLabel: string, manualOptions?: ManualProjectPanelOptions) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-slate-500">{emptyLabel}</p>;
  }

  const total = items.length;
  const orderedIds = items.map((row) =>
    row && typeof row === "object" ? String((row as Record<string, unknown>).project_id || "") : ""
  );
  const dragSourceIndex = manualOptions?.draggingProjectId
    ? orderedIds.indexOf(manualOptions.draggingProjectId)
    : -1;

  return (
    <div className="space-y-3">
      {manualOptions && total > 1 && (
        <div
          className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
          data-testid="project-reorder-hint"
        >
          <p className="flex items-center gap-1.5 text-xs text-slate-600">
            <span className="rounded bg-slate-100 px-1.5 py-0.5 font-semibold text-slate-700">1</span>
            Order matters: Project 1 is presented first in the final pitch. Drag the handle, or use the arrows,
            to reorder.
          </p>
          {manualOptions.reordering ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-primary-700" role="status">
              <svg className="h-3.5 w-3.5 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
              </svg>
              Saving order...
            </span>
          ) : manualOptions.orderSavedAt ? (
            <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-700" role="status">
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
              Order saved
            </span>
          ) : null}
        </div>
      )}
      {items.map((row, index) => {
        if (typeof row === "string") {
          const content = row.trim();
          return (
            <div key={`project-text-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-600">Project {index + 1}</p>
              <pre className="whitespace-pre-wrap break-words rounded bg-white p-3 text-sm text-slate-900">{content}</pre>
            </div>
          );
        }
        const item = (row ?? {}) as Record<string, unknown>;
        const rank = typeof item.rank === "number" ? item.rank : index + 1;
        const title = String(item.title || "Untitled project");
        const projectText = String(item.project_text_raw || item.project_text || "").trim();
        const description = String(item.description || "").trim();
        const content = projectText || [title, description].filter(Boolean).join("\n");
        const projectId = String(item.project_id || "");
        const isManual = Boolean(item.manual);
        const source = String(item.source || "").trim().toLowerCase();
        const removing = Boolean(manualOptions && manualOptions.removingProjectId === projectId);
        const isEdited = Boolean(item.edited);
        const originalText = String(item.original_project_text_raw || "").trim();
        const editing = Boolean(manualOptions && projectId && manualOptions.editingProjectId === projectId);
        const canManage = Boolean(manualOptions && projectId);
        const busy = Boolean(manualOptions && (manualOptions.disabled || manualOptions.savingEdit || removing));

        const orderable = Boolean(manualOptions && projectId && total > 1 && !editing && !busy);
        // The card is always draggable so the browser never misses the gesture; a drag
        // that did not start on the grip handle is cancelled in onDragStart instead.
        const draggable = orderable;
        const armedFromHandle = Boolean(manualOptions && manualOptions.armedProjectId === projectId);
        const isDragging = Boolean(manualOptions && manualOptions.draggingProjectId === projectId);
        const isDragOver = Boolean(
          manualOptions && manualOptions.dragOverProjectId === projectId && manualOptions.draggingProjectId !== projectId
        );
        // Show where the card would land rather than just highlighting the target.
        const dropLine = isDragOver && dragSourceIndex >= 0 ? (index < dragSourceIndex ? "top" : "bottom") : null;

        return (
          <div
            key={`${projectId || title}-${index}`}
            draggable={draggable}
            onDragStart={(event) => {
              if (!draggable || !manualOptions || !armedFromHandle) {
                // Selecting text or grabbing the card body must not start a reorder.
                event.preventDefault();
                return;
              }
              event.dataTransfer.effectAllowed = "move";
              event.dataTransfer.setData("text/plain", projectId);
              manualOptions.onDragStartProject(projectId);
            }}
            onDragOver={(event) => {
              if (!orderable || !manualOptions || !manualOptions.draggingProjectId) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = "move";
              manualOptions.onDragOverProject(projectId);
            }}
            onDrop={(event) => {
              if (!orderable || !manualOptions) return;
              event.preventDefault();
              manualOptions.onDropProject(projectId);
            }}
            onDragEnd={() => manualOptions?.onDragEndProject()}
            data-testid={projectId ? `pool-project-${projectId}` : undefined}
            className={`relative rounded-lg border p-4 transition-all ${
              editing ? "border-primary-300 bg-primary-50/40 ring-2 ring-primary-100" : "border-slate-200 bg-slate-50"
            } ${isDragging ? "scale-[0.99] opacity-50 ring-2 ring-primary-400" : ""} ${
              dropLine === "top" ? "border-t-4 border-t-primary-500" : ""
            } ${dropLine === "bottom" ? "border-b-4 border-b-primary-500" : ""}`}
          >
            <div className="mb-2 flex items-center justify-between gap-3">
              <p className="flex flex-wrap items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-600">
                {orderable && manualOptions && (
                  <span
                    role="button"
                    tabIndex={-1}
                    aria-label={`Drag ${title} to reorder`}
                    title="Drag to reorder"
                    onMouseDown={() => manualOptions.onArmDrag(projectId)}
                    onMouseUp={() => manualOptions.onArmDrag(null)}
                    onTouchStart={() => manualOptions.onArmDrag(projectId)}
                    className="-ml-1 inline-flex cursor-grab items-center rounded border border-slate-200 bg-white px-1 py-0.5 text-slate-400 hover:border-slate-300 hover:text-slate-600 active:cursor-grabbing"
                  >
                    <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" />
                    </svg>
                  </span>
                )}
                <span className="rounded bg-slate-200/80 px-1.5 py-0.5 text-[11px] font-bold normal-case tracking-normal text-slate-700">
                  {rank}
                </span>
                Project {rank}
                {isManual ? (
                  <span className="rounded bg-violet-100 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-violet-800">
                    Manual{source === "expert" ? " · Expert" : ""}
                  </span>
                ) : (
                  <span className="rounded bg-slate-200/70 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-slate-600">
                    Agent pick
                  </span>
                )}
                {isEdited ? (
                  <span className="rounded bg-amber-100 px-2 py-0.5 text-[10px] font-semibold normal-case tracking-normal text-amber-800">
                    Edited by human
                  </span>
                ) : null}
              </p>
              {canManage && manualOptions && !editing ? (
                <div className="flex items-center gap-1.5">
                  {total > 1 && (
                    <>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        aria-label={`Move ${title} up`}
                        disabled={busy || manualOptions.reordering || index === 0}
                        onClick={() => manualOptions.onMoveProject(projectId, -1)}
                      >
                        ↑
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        aria-label={`Move ${title} down`}
                        disabled={busy || manualOptions.reordering || index === total - 1}
                        onClick={() => manualOptions.onMoveProject(projectId, 1)}
                      >
                        ↓
                      </Button>
                    </>
                  )}
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`Edit ${title}`}
                    disabled={busy || Boolean(manualOptions.editingProjectId)}
                    onClick={() => manualOptions.onStartEdit(projectId, String(item.title || ""), content)}
                  >
                    Edit
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`Remove ${title}`}
                    className="hover:border-red-300 hover:bg-red-50 hover:text-red-700"
                    disabled={busy}
                    onClick={() => manualOptions.onRemove(projectId, isManual, title)}
                  >
                    {removing ? "Removing..." : "Remove"}
                  </Button>
                </div>
              ) : null}
            </div>
            {editing && manualOptions ? (
              <div className="space-y-2">
                <input
                  type="text"
                  value={manualOptions.editTitle}
                  onChange={(event) => manualOptions.onEditTitleChange(event.target.value)}
                  placeholder="Project title"
                  aria-label="Edit project title"
                  className="bp-input py-2"
                  disabled={manualOptions.savingEdit}
                />
                <textarea
                  value={manualOptions.editText}
                  onChange={(event) => manualOptions.onEditTextChange(event.target.value)}
                  aria-label="Edit project text"
                  className="bp-textarea h-44 text-sm"
                  disabled={manualOptions.savingEdit}
                  autoFocus
                />
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-500">
                    {manualOptions.editText.length.toLocaleString()} chars · this exact text goes to the final step
                  </span>
                  <div className="flex gap-2">
                    <Button type="button" size="sm" variant="secondary" onClick={manualOptions.onCancelEdit} disabled={manualOptions.savingEdit}>
                      Cancel
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      onClick={manualOptions.onSaveEdit}
                      disabled={manualOptions.savingEdit || !manualOptions.editText.trim()}
                    >
                      {manualOptions.savingEdit ? "Saving..." : "Save Changes"}
                    </Button>
                  </div>
                </div>
              </div>
            ) : (
              <pre className="whitespace-pre-wrap break-words rounded bg-white p-3 text-sm text-slate-900">{content}</pre>
            )}
            {isEdited && originalText && !editing ? (
              <details className="group mt-2">
                <summary className="cursor-pointer list-none text-xs font-medium text-slate-500 hover:text-slate-800">
                  <span className="group-open:hidden">Show agent's original text</span>
                  <span className="hidden group-open:inline">Hide agent's original text</span>
                </summary>
                <pre className="mt-2 whitespace-pre-wrap break-words rounded border border-dashed border-slate-300 bg-white/60 p-3 text-xs text-slate-500">
                  {originalText}
                </pre>
              </details>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

const renderManualProjectPanel = (options: ManualProjectPanelOptions) => {
  const canAdd = options.text.trim().length > 0 && !options.submitting && !options.disabled;
  return (
    <div className="space-y-3 rounded-lg border border-violet-200 bg-violet-50 p-4">
      <div>
        <h4 className="text-sm font-semibold text-slate-900">Add a Project Manually</h4>
        <p className="mt-1 text-xs text-slate-600">
          You can also edit or remove any project above, including the agent&apos;s picks, before approving this step.
        </p>
        <p className="mt-1 text-xs text-slate-600">
          Paste a project experience exactly as written. It joins the pool verbatim and the final step treats it as
          part of the authoritative project list.
        </p>
      </div>
      <input
        type="text"
        value={options.title}
        onChange={(event) => options.onTitleChange(event.target.value)}
        placeholder="Project title (optional)"
        aria-label="Manual project title"
        className="w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-500 focus:border-violet-500 focus:ring-2 focus:ring-violet-500 focus:ring-opacity-50 disabled:bg-slate-100"
        disabled={options.submitting || options.disabled}
      />
      <textarea
        value={options.text}
        onChange={(event) => options.onTextChange(event.target.value)}
        placeholder="Paste the project experience text..."
        aria-label="Manual project text"
        className="h-28 w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-500 focus:border-violet-500 focus:ring-2 focus:ring-violet-500 focus:ring-opacity-50 disabled:bg-slate-100"
        disabled={options.submitting || options.disabled}
      />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <label className="flex items-center gap-2 text-xs text-slate-700">
          <span>Source</span>
          <select
            value={options.source}
            onChange={(event) => options.onSourceChange(event.target.value === "expert" ? "expert" : "jyc")}
            aria-label="Manual project source"
            className="rounded-lg border border-slate-300 px-2 py-1 text-xs text-slate-900"
            disabled={options.submitting || options.disabled}
          >
            <option value="jyc">JYC project</option>
            <option value="expert">Extended team / expert project</option>
          </select>
        </label>
        <Button type="button" size="sm" onClick={options.onAdd} disabled={!canAdd}>
          {options.submitting ? "Adding..." : "Add Project to Pool"}
        </Button>
      </div>
      {options.source === "expert" ? (
        <p className="text-xs text-violet-800">Expert-sourced projects switch the final step to blended-team voice.</p>
      ) : null}
    </div>
  );
};

const renderMappedProjectCards = (items: unknown, emptyLabel: string) => {
  if (!Array.isArray(items) || items.length === 0) {
    return <p className="text-sm text-slate-500">{emptyLabel}</p>;
  }

  return (
    <div className="space-y-3">
      {items.map((row, index) => {
        const item = (row ?? {}) as Record<string, unknown>;
        const itemType = String(item.item_type || "item");
        const itemNumber = typeof item.item_number === "number" ? item.item_number : index + 1;
        const itemText = String(item.item_text || "").trim();
        const projectText = String(item.mapped_project_text || "").trim();
        const mappedTitle = String(item.mapped_project_title || "").trim();
        const mappedDescription = String(item.mapped_project_description || "").trim();
        const content =
          projectText || [mappedTitle, mappedDescription].filter(Boolean).join("\n") || "No mapped project text.";

        return (
          <div key={`${itemType}-${itemNumber}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
              {itemType === "bullet" ? "Ideal Candidate Bullet" : "Structured Question"} {itemNumber}
            </p>
            {itemText ? <p className="mb-3 mt-1 text-sm text-slate-800">{itemText}</p> : null}
            <pre className="whitespace-pre-wrap break-words rounded bg-white p-3 text-sm text-slate-900">{content}</pre>
          </div>
        );
      })}
    </div>
  );
};

interface Step5AnnotationRenderOptions {
  enabled: boolean;
  persistedParagraphIds: Set<string>;
  draftParagraphIds: Set<string>;
  hoveredParagraphId: string | null;
  paragraphsBySectionKey: Map<string, Step5ReviewSentence[]>;
  onParagraphHover: (paragraphId: string) => void;
  onParagraphLeave: (paragraphId: string) => void;
  onParagraphClick: (paragraphId: string) => void;
  disabled: boolean;
}

const renderAnnotatableParagraphs = (
  sectionKey: string,
  fallbackContent: string,
  options?: Step5AnnotationRenderOptions,
  range?: { start: number; end: number }
) => {
  if (!options?.enabled) {
    return (
      <pre className="whitespace-pre-wrap break-words rounded bg-white p-3 text-sm text-slate-900">
        {fallbackContent || "No content generated."}
      </pre>
    );
  }

  const allParagraphs = options.paragraphsBySectionKey.get(sectionKey) || [];
  const paragraphs = range ? allParagraphs.slice(range.start, range.end) : allParagraphs;
  if (paragraphs.length === 0) {
    return (
      <pre className="whitespace-pre-wrap break-words rounded bg-white p-3 text-sm text-slate-900">
        {fallbackContent || "No content generated."}
      </pre>
    );
  }

  return (
    <div className="space-y-2 whitespace-pre-wrap break-words rounded bg-white p-3 text-sm text-slate-900">
      {paragraphs.map((paragraph) => {
        const isPersisted = options.persistedParagraphIds.has(paragraph.id);
        const isDraft = options.draftParagraphIds.has(paragraph.id);
        const isHovered = options.hoveredParagraphId === paragraph.id;
        const paragraphClass = isPersisted
          ? "border-amber-300 bg-amber-100 text-amber-900"
          : isDraft
            ? "border-blue-300 bg-blue-100 text-blue-900"
            : isHovered
              ? "border-slate-300 bg-slate-100 text-slate-900"
              : "border-transparent bg-transparent text-slate-900 hover:border-slate-200 hover:bg-slate-50";
        return (
          <button
            key={paragraph.id}
            type="button"
            onMouseEnter={() => options.onParagraphHover(paragraph.id)}
            onMouseLeave={() => options.onParagraphLeave(paragraph.id)}
            onClick={() => options.onParagraphClick(paragraph.id)}
            disabled={options.disabled}
            className={`w-full rounded border px-2 py-1 text-left transition ${paragraphClass}`}
          >
            <span className="font-mono text-[15px] leading-7">{paragraph.sentence_text}</span>
          </button>
        );
      })}
    </div>
  );
};

const buildPitchPlainText = (output: Record<string, unknown>): string => {
  const { combinedText } = getStandardPitchCombinedText(output);
  if (combinedText) return combinedText;

  const finalPitchText = String(output.final_pitch_text || "").trim();
  if (finalPitchText) return finalPitchText;

  const blocks: string[] = [];
  const intro = (output.intro as Record<string, unknown> | undefined) ?? {};
  const introContent = String(intro.content || "").trim();
  if (introContent) blocks.push(introContent);

  const responses = Array.isArray(output.responses)
    ? (output.responses as Array<Record<string, unknown>>)
    : [];
  responses.forEach((row, index) => {
    const responseText = String(row.response || "").trim();
    if (!responseText) return;
    const itemNumber = Number(row.item_number || index + 1);
    const itemText = String(row.item_text || "").trim();
    const heading = itemText ? `${String(itemNumber).padStart(2, "0")}. ${itemText}` : "";
    blocks.push(heading ? `${heading}\n${responseText}` : responseText);
  });

  return blocks.join("\n\n").trim();
};

const copyTextToClipboard = async (text: string): Promise<boolean> => {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // Fall through to the textarea path below; some browsers reject the async API
    // outside a secure context even when it exists.
  }

  try {
    const area = document.createElement("textarea");
    area.value = text;
    area.setAttribute("readonly", "");
    area.style.position = "fixed";
    area.style.opacity = "0";
    document.body.appendChild(area);
    area.select();
    const copied = document.execCommand("copy");
    document.body.removeChild(area);
    return copied;
  } catch {
    return false;
  }
};

const CopyPitchTextButton = ({
  text,
  idleLabel = "Copy text",
  title = "Copy the pitch as plain text, ready to paste",
}: {
  text: string;
  idleLabel?: string;
  title?: string;
}) => {
  const [state, setState] = useState<"idle" | "copied" | "failed">("idle");

  useEffect(() => {
    if (state === "idle") return;
    const timer = window.setTimeout(() => setState("idle"), 2000);
    return () => window.clearTimeout(timer);
  }, [state]);

  if (!text.trim()) return null;

  const label = state === "copied" ? "Copied" : state === "failed" ? "Copy failed" : idleLabel;

  return (
    <button
      type="button"
      onClick={async () => {
        const copied = await copyTextToClipboard(text);
        setState(copied ? "copied" : "failed");
      }}
      title={title}
      className={`rounded border px-2 py-1 text-xs font-medium transition ${
        state === "copied"
          ? "border-green-300 bg-green-50 text-green-800"
          : state === "failed"
            ? "border-red-300 bg-red-50 text-red-800"
            : "border-slate-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-slate-50"
      }`}
    >
      {label}
    </button>
  );
};

const renderPitchEditor = (
  output: Record<string, unknown>,
  heading: string,
  editOptions: PitchEditOptions
) => {
  const draftTotal = STANDARD_PITCH_PART_DEFS.reduce(
    (sum, part) => sum + (editOptions.drafts[part.key] ?? "").trim().length,
    0
  );
  const totalOver = editOptions.totalLimit !== null && draftTotal > editOptions.totalLimit;
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-900">Edit {heading}</h4>
          <span className={`text-xs ${totalOver ? "font-semibold text-red-700" : "text-slate-600"}`}>
            {draftTotal} chars{editOptions.totalLimit !== null ? ` / ${editOptions.totalLimit}` : ""}
          </span>
        </div>
        <div className="space-y-4">
          {STANDARD_PITCH_PART_DEFS.map((part) => {
            const section = (output[part.key] as Record<string, unknown> | undefined) ?? {};
            const label = String(section.section_name || part.fallback);
            const value = editOptions.drafts[part.key] ?? "";
            const limit = editOptions.partLimits[part.key];
            const count = value.trim().length;
            const over = limit !== undefined && count > limit;
            return (
              <div key={part.key}>
                <div className="mb-1 flex items-center justify-between">
                  <label htmlFor={`pitch-edit-${part.key}`} className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                    {label}
                  </label>
                  <span className={`text-xs ${over ? "font-semibold text-red-700" : "text-slate-500"}`}>
                    {count}
                    {limit !== undefined ? ` / ${limit}` : ""}
                  </span>
                </div>
                <textarea
                  id={`pitch-edit-${part.key}`}
                  value={value}
                  onChange={(event) => editOptions.onChange(part.key, event.target.value)}
                  disabled={editOptions.saving}
                  className="bp-textarea h-40 w-full text-sm"
                />
              </div>
            );
          })}
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          <Button variant="secondary" size="sm" onClick={editOptions.onCancel} disabled={editOptions.saving}>
            Cancel
          </Button>
          <Button size="sm" onClick={editOptions.onSave} disabled={editOptions.saving}>
            {editOptions.saving ? "Saving..." : "Save as new version"}
          </Button>
        </div>
      </div>
    </div>
  );
};

const SECTION_BADGE_CLASS = "rounded-full border px-2 py-0.5 text-[11px] font-medium";

const introBadge = (output: Record<string, unknown>, hasBidderNotes: boolean) => {
  if (!hasBidderNotes) {
    return { label: "No brainstorm notes", className: "border-slate-300 text-slate-600" };
  }
  return output.intro_requested_by_bidder_notes === true
    ? { label: "Requested in brainstorm notes", className: "border-green-300 bg-green-50 text-green-800" }
    : { label: "Not requested in brainstorm notes", className: "border-amber-300 bg-amber-50 text-amber-800" };
};

// Umbrex takes one free-text box, but DC pastes sections selectively: projects always,
// the intro when it helps, the closing rarely. Each part gets its own count and copy
// button. Paragraph ids stay those of the combined "final_pitch_text" section, so
// targeted feedback is unchanged; each part renders its slice of that list.
const renderPitchSections = (
  output: Record<string, unknown>,
  annotationOptions: Step5AnnotationRenderOptions | undefined,
  sectionOptions: PitchSectionOptions
) => {
  let paragraphOffset = 0;
  return (
    <div className="space-y-3">
      {STANDARD_PITCH_PART_DEFS.map((part) => {
        const section = (output[part.key] as Record<string, unknown> | undefined) ?? {};
        const content = String(section.content || "").trim();
        if (!content) return null;
        const label = String(section.section_name || part.fallback);
        const limit = UMBREX_PART_LIMITS[part.key];
        const count = content.length;
        const range = { start: paragraphOffset, end: paragraphOffset + splitIntoParagraphs(content).length };
        paragraphOffset = range.end;
        const badge =
          part.key === "part_1"
            ? introBadge(output, sectionOptions.hasBidderNotes)
            : part.key === "part_3"
              ? { label: "Optional", className: "border-slate-300 text-slate-600" }
              : null;
        return (
          <div key={part.key} data-testid={`pitch-section-${part.key}`} className="rounded-lg border border-slate-200 bg-white p-3">
            <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
              <div className="flex flex-wrap items-center gap-2">
                <h5 className="text-xs font-semibold uppercase tracking-wide text-slate-600">{label}</h5>
                {badge && <span className={`${SECTION_BADGE_CLASS} ${badge.className}`}>{badge.label}</span>}
              </div>
              <div className="flex items-center gap-2">
                <span className={`text-xs ${limit !== undefined && count > limit ? "font-semibold text-red-700" : "text-slate-500"}`}>
                  {count}
                  {limit !== undefined ? ` / ${limit}` : ""}
                </span>
                <CopyPitchTextButton text={content} idleLabel="Copy" title={`Copy ${label} only`} />
              </div>
            </div>
            {renderAnnotatableParagraphs("final_pitch_text", content, annotationOptions, range)}
          </div>
        );
      })}
    </div>
  );
};

const renderPitchParts = (
  output: Record<string, unknown>,
  annotationOptions?: Step5AnnotationRenderOptions,
  heading: string = "Final Pitch",
  editOptions?: PitchEditOptions,
  sectionOptions?: PitchSectionOptions
) => {
  if (editOptions?.editing) {
    return renderPitchEditor(output, heading, editOptions);
  }
  const { combinedText, totalCharacterCount } = getStandardPitchCombinedText(output);
  return (
    <div className="space-y-3">
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-900">
            {heading}
            {output.human_edited === true && (
              <span className="ml-2 rounded-full border border-slate-300 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                Edited by hand
              </span>
            )}
          </h4>
          <div className="flex items-center gap-2">
            <span className="text-xs text-slate-600">{totalCharacterCount} chars</span>
            {editOptions && (
              <Button variant="secondary" size="sm" onClick={editOptions.onStart} disabled={editOptions.disabled}>
                Edit text
              </Button>
            )}
            <CopyPitchTextButton text={buildPitchPlainText(output)} />
          </div>
        </div>
        {sectionOptions
          ? renderPitchSections(output, annotationOptions, sectionOptions)
          : renderAnnotatableParagraphs("final_pitch_text", combinedText, annotationOptions)}
      </div>
    </div>
  );
};

const renderIdbPitchResponses = (output: Record<string, unknown>, annotationOptions?: Step5AnnotationRenderOptions) => {
  const finalPitchText = String(output.final_pitch_text || "").trim();
  if (finalPitchText) {
    const count = Number(output.character_count || finalPitchText.length);
    return (
      <div className="space-y-4">
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
          <div className="mb-2 flex items-center justify-between">
            <h4 className="text-sm font-semibold text-slate-900">Final Pitch</h4>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-600">{count} chars</span>
              <CopyPitchTextButton text={buildPitchPlainText(output)} />
            </div>
          </div>
          {renderAnnotatableParagraphs("final_pitch_text", finalPitchText, annotationOptions)}
        </div>
      </div>
    );
  }

  const intro = (output.intro as Record<string, unknown> | undefined) ?? {};
  const introContent = String(intro.content || "").trim();
  const responses = Array.isArray(output.responses)
    ? (output.responses as Array<Record<string, unknown>>)
    : [];

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <CopyPitchTextButton text={buildPitchPlainText(output)} />
      </div>
      <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
        <div className="mb-2 flex items-center justify-between">
          <h4 className="text-sm font-semibold text-slate-900">Intro</h4>
          <span className="text-xs text-slate-600">{Number(intro.character_count || introContent.length)} chars</span>
        </div>
        {renderAnnotatableParagraphs("intro", introContent, annotationOptions)}
      </div>

      <div className="space-y-3">
        {responses.length === 0 ? (
          <p className="text-sm text-slate-500">No IDB responses generated yet.</p>
        ) : (
          responses.map((row, index) => {
            const itemType = String(row.item_type || "item");
            const itemNumber = Number(row.item_number || index + 1);
            const itemText = String(row.item_text || "");
            const responseText = String(row.response || "");
            return (
              <div key={`${itemType}-${itemNumber}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-600">
                  {itemType === "question" ? "Question" : "Bullet"} {itemNumber}
                </p>
                <p className="mt-1 text-sm text-slate-800">{itemText || "No item text."}</p>
                <div className="mt-3">
                  {renderAnnotatableParagraphs(`responses.${index + 1}`, responseText, annotationOptions)}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

const expertId = (expert: Record<string, unknown>): string => String(expert.expert_id || "").trim();

const renderExpertSelectionPanel = (
  recommendation: unknown,
  selectionOptions?: Step4ExpertSelectionOptions,
  // Umbrex never recommends experts: the extended team is the reviewer's own call.
  expertsOptional = false
) => {
  const payload = (recommendation ?? {}) as Record<string, unknown>;
  const recommended = Array.isArray(payload.recommended_experts)
    ? (payload.recommended_experts as Array<Record<string, unknown>>)
    : [];
  const available = Array.isArray(payload.available_experts)
    ? (payload.available_experts as Array<Record<string, unknown>>)
    : [];
  const selectedIds = new Set(selectionOptions?.selectedExpertIds || []);
  const status = String(payload.status || "").trim();
  const message = String(payload.message || "").trim();
  const availableById = new Map(available.map((expert) => [expertId(expert), expert]));
  const selectedExperts = Array.from(selectedIds)
    .map((id) => availableById.get(id) ?? recommended.find((expert) => expertId(expert) === id))
    .filter((expert): expert is Record<string, unknown> => Boolean(expert));
  const searchText = selectionOptions?.searchText.trim().toLowerCase() || "";
  const unselectedAvailable = available.filter((expert) => !selectedIds.has(expertId(expert)));
  const filteredAvailable = searchText
    ? unselectedAvailable.filter((expert) =>
        [
          expert.name,
          expert.expert_type,
          expert.summary,
          expert.rationale,
        ]
          .map((value) => String(value || "").toLowerCase())
          .join(" ")
          .includes(searchText)
      )
    : unselectedAvailable;

  const renderExpertRow = (expert: Record<string, unknown>, source: "selected" | "available", index: number) => {
    const id = expertId(expert);
    if (!id) return null;
    const selected = selectedIds.has(id);
    const name = String(expert.name || id);
    const summary = String(expert.summary || expert.rationale || "").trim();
    const orderable = Boolean(selectionOptions && source === "selected" && selectedExperts.length > 1);
    const draggable = orderable && !selectionOptions?.disabled;
    const isDragging = Boolean(orderable && selectionOptions?.draggingExpertId === id);
    const isDragOver = Boolean(
      orderable && selectionOptions?.dragOverExpertId === id && selectionOptions?.draggingExpertId !== id
    );
    return (
      <div
        key={`${source}-${id}-${index}`}
        draggable={draggable}
        onDragStart={(event) => {
          if (!draggable || !selectionOptions) return;
          event.dataTransfer.effectAllowed = "move";
          event.dataTransfer.setData("text/plain", id);
          selectionOptions.onDragStartExpert(id);
        }}
        onDragOver={(event) => {
          if (!draggable || !selectionOptions) return;
          event.preventDefault();
          event.dataTransfer.dropEffect = "move";
          selectionOptions.onDragOverExpert(id);
        }}
        onDrop={(event) => {
          if (!draggable || !selectionOptions) return;
          event.preventDefault();
          const sourceId = selectionOptions.draggingExpertId;
          selectionOptions.onDragEndExpert();
          if (sourceId && sourceId !== id) {
            selectionOptions.onReorderExperts(sourceId, id);
          }
        }}
        onDragEnd={() => selectionOptions?.onDragEndExpert()}
        data-testid={source === "selected" ? `selected-expert-${id}` : undefined}
        className={`rounded-lg border border-slate-200 bg-white p-3 transition-shadow ${
          isDragging ? "opacity-60" : ""
        } ${isDragOver ? "ring-2 ring-blue-300" : ""}`}
      >
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="flex items-center gap-1.5 text-sm font-semibold text-slate-900">
              {orderable && (
                <span className="cursor-grab text-slate-400" aria-hidden="true" title="Drag to reorder">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M8 6h.01M8 12h.01M8 18h.01M16 6h.01M16 12h.01M16 18h.01" />
                  </svg>
                </span>
              )}
              {orderable ? `${index + 1}. ` : ""}
              {name}
            </p>
            {summary ? <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{summary}</p> : null}
            {expert.expert_type ? <p className="mt-1 text-xs text-slate-500">{String(expert.expert_type)}</p> : null}
          </div>
          {selectionOptions ? (
            <div className="flex flex-shrink-0 items-center gap-1.5">
              {orderable && (
                <>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`Move ${name} up`}
                    disabled={selectionOptions.disabled || index === 0}
                    onClick={() => selectionOptions.onMoveExpert(id, -1)}
                  >
                    ↑
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    aria-label={`Move ${name} down`}
                    disabled={selectionOptions.disabled || index === selectedExperts.length - 1}
                    onClick={() => selectionOptions.onMoveExpert(id, 1)}
                  >
                    ↓
                  </Button>
                </>
              )}
              <Button
                type="button"
                aria-label={`${selected ? "Remove" : "Add"} ${name}`}
                variant={selected ? "secondary" : "primary"}
                disabled={selectionOptions.disabled}
                onClick={() => selectionOptions.onToggleExpert(id)}
              >
                {selected ? "Remove" : "Add"}
              </Button>
            </div>
          ) : null}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-4 rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-semibold text-slate-900">
            {expertsOptional ? "Extended Team (optional)" : "Recommended Experts"}
          </h4>
          <p className="mt-1 text-xs text-slate-600">
            {expertsOptional
              ? "Nothing is selected by default. Add experts from the catalog only if this proposal needs an extended team."
              : "Add experts from the catalog, remove selected experts, or reset to the agent choices."}
          </p>
        </div>
        {selectionOptions && !expertsOptional ? (
          <Button
            type="button"
            variant="secondary"
            disabled={selectionOptions.disabled}
            onClick={selectionOptions.onResetExperts}
            aria-label="Reset to Agent Choices"
          >
            Reset
          </Button>
        ) : null}
        {status === "error" && message ? <p className="mt-1 text-sm text-amber-700">{message}</p> : null}
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-slate-900">Available Experts</h4>
            <span className="text-xs text-slate-500">{filteredAvailable.length} shown</span>
          </div>
          {selectionOptions ? (
            <input
              type="search"
              value={selectionOptions.searchText}
              onChange={(event) => selectionOptions.onSearchTextChange(event.target.value)}
              placeholder="Search experts..."
              className="mt-3 w-full rounded border border-slate-300 px-3 py-2 text-sm text-slate-900 placeholder-slate-500 focus:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
              disabled={selectionOptions.disabled}
            />
          ) : null}
          <div data-testid="available-experts-panel" className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
            {filteredAvailable.length > 0 ? (
              filteredAvailable.map((expert, index) => renderExpertRow(expert, "available", index))
            ) : available.length > 0 ? (
              <p className="text-sm text-slate-500">No experts match the current search.</p>
            ) : (
              <p className="text-sm text-slate-500">No available experts loaded.</p>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
          <div className="flex items-center justify-between gap-3">
            <h4 className="text-sm font-semibold text-slate-900">Selected Experts</h4>
            <span className="text-xs text-blue-700">{selectedExperts.length} selected</span>
          </div>
          {selectionOptions && selectedExperts.length > 1 && (
            <p className="mt-2 text-xs text-blue-800" data-testid="expert-reorder-hint">
              Drag to reorder. Order matters: expert 1 is introduced first in the final pitch.
            </p>
          )}
          <div data-testid="selected-experts-panel" className="mt-3 max-h-80 space-y-2 overflow-y-auto pr-1">
            {selectedExperts.length > 0 ? (
              selectedExperts.map((expert, index) => renderExpertRow(expert, "selected", index))
            ) : (
              <p className="text-sm text-slate-500">No experts selected.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const renderStructuredOutput = (
  output: RFPOutput | undefined,
  annotationOptions?: Step5AnnotationRenderOptions,
  expertSelectionOptions?: Step4ExpertSelectionOptions,
  manualProjectOptions?: ManualProjectPanelOptions,
  pitchEditOptions?: PitchEditOptions,
  pitchSectionOptions?: PitchSectionOptions
) => {
  const normalizedOutput = normalizeStepOutput(output);
  if (!normalizedOutput) {
    return <p className="text-sm text-slate-500">No output recorded yet for this step.</p>;
  }
  const payload = normalizedOutput.output;

  if (normalizedOutput.agent_slug === "catalant-rfp-breakapart" || normalizedOutput.agent_slug === "umbrex-rfp-breakapart") {
    return (
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Client Goal</h4>
          {renderBulletList(payload.client_goal, "No client goal bullets.")}
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Project Scope</h4>
          {renderBulletList(payload.project_scope, "No project scope bullets.")}
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Ideal Profile</h4>
          {renderBulletList(payload.ideal_profile, "No ideal profile bullets.")}
        </div>
      </div>
    );
  }

  if (normalizedOutput.agent_slug === "catalant-rfp-intake") {
    const metadata = payload.metadata as Record<string, unknown> | undefined;
    return (
      <div className="space-y-4">
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Metadata</h4>
          <pre className="whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800">
            {JSON.stringify(metadata ?? {}, null, 2)}
          </pre>
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-900">Ideal Candidate Bullets</h4>
            {renderBulletList(
              Array.isArray(payload.ideal_candidate_bullets)
                ? payload.ideal_candidate_bullets.map((item) =>
                    typeof item === "object" && item !== null && "bullet_text" in item
                      ? String((item as { bullet_text: unknown }).bullet_text)
                      : String(item)
                  )
                : [],
              "No ideal candidate bullets."
            )}
          </div>
          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-900">Structured Questions</h4>
            {renderBulletList(payload.structured_questions, "No structured questions.")}
          </div>
        </div>
      </div>
    );
  }

  if (normalizedOutput.agent_slug === "catalant-keyword-extraction") {
    const keywordGroups = (payload.keywords as Record<string, unknown> | undefined) ?? {};
    return (
      <div className="space-y-4">
        <div className="grid gap-4 md:grid-cols-3">
          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-900">Background &amp; Relevant Experience</h4>
            {renderBulletList(keywordGroups.background_and_experience, "No background keywords.")}
          </div>
          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-900">Similar / Relevant Projects</h4>
            {renderBulletList(keywordGroups.similar_projects, "No similar project keywords.")}
          </div>
          <div>
            <h4 className="mb-2 text-sm font-semibold text-slate-900">Proposed Approach &amp; Conclusion</h4>
            {renderBulletList(keywordGroups.approach_and_conclusion, "No approach keywords.")}
          </div>
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Brainstorm Points</h4>
          {renderBulletList(payload.brainstorm_points, "No brainstorm points.")}
        </div>
      </div>
    );
  }

  if (normalizedOutput.agent_slug === "umbrex-keyword-extraction") {
    const keywordGroups = (payload.keywords as Record<string, unknown> | undefined) ?? {};
    return (
      <div className="grid gap-4 md:grid-cols-3">
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Relevant Experience</h4>
          {renderBulletList(keywordGroups.relevant_experience, "No relevant experience keywords.")}
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Selected Projects</h4>
          {renderBulletList(keywordGroups.selected_projects, "No selected project keywords.")}
        </div>
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Closing Summary</h4>
          {renderBulletList(keywordGroups.closing_summary, "No closing summary keywords.")}
        </div>
      </div>
    );
  }

  if (PROJECT_SELECTOR_AGENT_SLUGS.has(normalizedOutput.agent_slug)) {
    const droppedFlavors = Array.isArray(payload.dropped_duplicate_flavors)
      ? (payload.dropped_duplicate_flavors as Array<Record<string, unknown>>)
      : [];
    return (
      <div className="space-y-4">
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">Selected Projects</h4>
          {droppedFlavors.length > 0 ? (
            <p className="mb-2 rounded border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
              {droppedFlavors.length} duplicate flavor{droppedFlavors.length === 1 ? "" : "s"} of an already-selected
              project {droppedFlavors.length === 1 ? "was" : "were"} removed so each engagement appears once.
            </p>
          ) : null}
          {renderProjectCards(payload.selected_projects, "No projects selected yet.", manualProjectOptions)}
        </div>
        {manualProjectOptions ? renderManualProjectPanel(manualProjectOptions) : null}
        {renderExpertSelectionPanel(
          payload.expert_recommendation,
          expertSelectionOptions,
          isUmbrexSlug(normalizedOutput.agent_slug)
        )}
      </div>
    );
  }

  if (normalizedOutput.agent_slug === "catalant-idb-mapping") {
    return (
      <div className="space-y-4">
        <div>
          <h4 className="mb-2 text-sm font-semibold text-slate-900">IDB Project Mapping</h4>
          {renderMappedProjectCards(payload.mappings, "No IDB mappings generated yet.")}
        </div>
        {renderExpertSelectionPanel(payload.expert_recommendation, expertSelectionOptions)}
      </div>
    );
  }

  if (THREE_PART_PITCH_AGENT_SLUGS.has(normalizedOutput.agent_slug)) {
    const isUmbrex = isUmbrexSlug(normalizedOutput.agent_slug);
    return renderPitchParts(
      payload,
      annotationOptions,
      isUmbrex ? "Final Proposal" : "Final Pitch",
      pitchEditOptions,
      isUmbrex ? pitchSectionOptions ?? { hasBidderNotes: false } : undefined
    );
  }

  if (normalizedOutput.agent_slug === "catalant-idb-pitch" || normalizedOutput.agent_slug === "catalant-idb-team-pitch") {
    return renderIdbPitchResponses(payload, annotationOptions);
  }

  return (
    <pre className="whitespace-pre-wrap rounded border border-slate-200 bg-slate-50 p-3 text-xs text-slate-800">
      {JSON.stringify(payload, null, 2)}
    </pre>
  );
};

const eventTypeLabel: Record<TimelineEvent["event_type"], string> = {
  agent_input: "Agent Input",
  agent_output: "Agent Output",
  human_feedback: "Human Feedback",
  accepted_output: "Accepted Output",
  memory_retrieval: "Memory Retrieval",
  tool_call: "Tool Call",
};

const renderTimelineDiagnosticContent = (event: TimelineEvent) => {
  const content = (event.content ?? {}) as Record<string, unknown>;
  if (event.event_type === "memory_retrieval") {
    const memories = Array.isArray(content.memories) ? (content.memories as Array<Record<string, unknown>>) : [];
    return (
      <div className="mt-3 rounded bg-white p-3 text-xs text-slate-900">
        <div className="flex flex-wrap gap-2 text-slate-700">
          <span className="rounded bg-slate-100 px-2 py-1">Selected {String(content.selected_count ?? memories.length)}</span>
          {content.truncated ? <span className="rounded bg-amber-100 px-2 py-1 text-amber-900">Truncated</span> : null}
        </div>
        {typeof content.query === "string" && content.query.trim() ? (
          <p className="mt-2 break-words text-slate-700">Query: {content.query}</p>
        ) : null}
        {memories.length === 0 ? (
          <p className="mt-2 text-slate-500">No long-term memories were selected for this run.</p>
        ) : (
          <div className="mt-2 space-y-2">
            {memories.map((memory, index) => (
              <div key={`${String(memory.memory_id || "memory")}-${index}`} className="rounded-lg border border-slate-200 bg-slate-50 p-2">
                <div className="flex flex-wrap items-center gap-2 text-slate-600">
                  <span className="font-mono">{String(memory.memory_id || "unknown_memory")}</span>
                  {memory.score !== undefined ? <span>score {Number(memory.score).toFixed(2)}</span> : null}
                </div>
                <p className="mt-1 whitespace-pre-wrap break-words text-slate-900">
                  {String(memory.content_preview || "(empty memory preview)")}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  if (event.event_type === "tool_call") {
    const summary = (content.summary ?? {}) as Record<string, unknown>;
    return (
      <div className="mt-3 rounded bg-white p-3 text-xs text-slate-900">
        <div className="flex flex-wrap gap-2 text-slate-700">
          <span className="rounded bg-slate-100 px-2 py-1">{String(content.tool_name || content.tool_id || "tool")}</span>
          <span className="rounded bg-slate-100 px-2 py-1">Status {String(content.status || "unknown")}</span>
          {content.source ? <span className="rounded bg-slate-100 px-2 py-1">Source {String(content.source)}</span> : null}
        </div>
        {Object.keys(summary).length > 0 ? (
          <pre className="mt-2 whitespace-pre-wrap break-words rounded border border-slate-200 bg-slate-50 p-2">
            {JSON.stringify(summary, null, 2)}
          </pre>
        ) : (
          <p className="mt-2 text-slate-500">No tool summary was captured.</p>
        )}
      </div>
    );
  }

  return null;
};

const emptyStepVersionStats: StepVersionStats = {
  outputVersion: 0,
  latestOutputRunId: null,
  latestOutputAtMs: null,
  feedbackCount: 0,
  latestFeedbackAtMs: null,
  feedbackPendingRerun: false,
};

interface StepVersionEntry {
  runId: number;
  version: number;
  timestamp: string;
  status: string;
  inputStatus: string;
}

const runIdFromMetadata = (metadata: Record<string, unknown> | undefined): number | null => {
  const raw = metadata?.agent_run_id;
  if (typeof raw === "number") return raw;
  if (typeof raw === "string") return Number(raw) || null;
  return null;
};

const getStepVersionEntries = (timeline: StepTimeline | undefined): StepVersionEntry[] => {
  if (!timeline) return [];
  const inputStatusByRunId = new Map<number, string>();
  timeline.events
    .filter((event) => event.event_type === "agent_input")
    .forEach((event) => {
      const runId = runIdFromMetadata(event.metadata);
      const inputStatus = event.metadata?.input_status;
      if (runId !== null && typeof inputStatus === "string") {
        inputStatusByRunId.set(runId, inputStatus);
      }
    });

  return timeline.events
    .filter((event) => event.event_type === "agent_output")
    .map((event, index) => {
      const runId = runIdFromMetadata(event.metadata);
      const status = event.metadata?.status;
      return {
        runId: runId ?? 0,
        version: index + 1,
        timestamp: event.timestamp,
        status: typeof status === "string" ? status : "",
        inputStatus: runId !== null ? inputStatusByRunId.get(runId) || "" : "",
      };
    })
    .filter((entry) => entry.runId > 0);
};

// Ghost text in the empty feedback box, cycling so one case does not look like the
// only thing feedback is for.
const FEEDBACK_EXAMPLES_BY_ROLE: Record<string, string[]> = {
  intake: [
    "Client is the buyer, not the consultancy - fix the roles.",
    "Deadline and budget are wrong; take them from the RFP text.",
    "Add the two questions the RFP asks about staffing.",
  ],
  breakapart: [
    "Merge the two overlapping workstreams into one.",
    "Add the data-migration scope the RFP mentions in section 3.",
    "The ideal profile is too junior for this engagement.",
  ],
  keywords: [
    "Drop the generic terms like 'strategy' and keep the industry ones.",
    "Add synonyms for 'post-merger integration'.",
    "These keywords miss the regulatory angle entirely.",
  ],
  projects: [
    "Replace project 3 with something in the same industry.",
    "Favour the last 3 years of work over older engagements.",
    "Two of these are the same engagement - swap one out.",
    "Put the healthcare project first; it is the closest fit.",
  ],
  final: [
    "Cut the intro to two sentences and lead with the outcome.",
    "Name the retail engagement instead of describing it generically.",
    "Less sales tone, more specifics on how we would run week one.",
    "Part 2 is over the limit - tighten it, keep the numbers.",
  ],
};

const DEFAULT_FEEDBACK_EXAMPLES = [
  "Keep the structure but tighten the wording.",
  "Focus on what the RFP asks for and drop the rest.",
];

const feedbackExamplesForRole = (role: string | undefined): string[] =>
  FEEDBACK_EXAMPLES_BY_ROLE[String(role || "")] || DEFAULT_FEEDBACK_EXAMPLES;

const reorderList = (ids: string[], sourceId: string, targetId: string): string[] => {
  const from = ids.indexOf(sourceId);
  const to = ids.indexOf(targetId);
  if (from < 0 || to < 0 || from === to) return ids;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, sourceId);
  return next;
};

const moveInList = (ids: string[], id: string, direction: -1 | 1): string[] => {
  const from = ids.indexOf(id);
  const to = from + direction;
  if (from < 0 || to < 0 || to >= ids.length) return ids;
  const next = [...ids];
  next.splice(from, 1);
  next.splice(to, 0, id);
  return next;
};

const versionOriginLabel = (inputStatus: string): string => {
  switch (inputStatus) {
    case "initial":
      return "Agent run";
    case "feedback":
      return "Feedback rerun";
    case "human_edit":
      return "Hand edit";
    case "human_confirmation":
      return "Expert confirmation";
    case "version_restore":
      return "Restored version";
    default:
      return inputStatus || "Agent run";
  }
};

const parseTimestampMs = (value: string | null | undefined): number | null => {
  if (!value) return null;
  const ts = new Date(value).getTime();
  return Number.isFinite(ts) ? ts : null;
};

const getStepVersionStats = (timeline: StepTimeline | undefined): StepVersionStats => {
  if (!timeline) {
    return emptyStepVersionStats;
  }

  const outputEvents = timeline.events.filter((event) => event.event_type === "agent_output");
  const feedbackEvents = timeline.events.filter((event) => event.event_type === "human_feedback");
  const latestOutputEvent = outputEvents.length > 0 ? outputEvents[outputEvents.length - 1] : null;
  const latestFeedbackEvent = feedbackEvents.length > 0 ? feedbackEvents[feedbackEvents.length - 1] : null;
  const latestOutputAtMs = parseTimestampMs(latestOutputEvent?.timestamp);
  const latestFeedbackAtMs = parseTimestampMs(latestFeedbackEvent?.timestamp);
  const latestOutputRunIdRaw = latestOutputEvent?.metadata?.agent_run_id;
  const latestOutputRunId =
    typeof latestOutputRunIdRaw === "number"
      ? latestOutputRunIdRaw
      : typeof latestOutputRunIdRaw === "string"
        ? Number(latestOutputRunIdRaw) || null
        : null;

  return {
    outputVersion: outputEvents.length,
    latestOutputRunId,
    latestOutputAtMs,
    feedbackCount: feedbackEvents.length,
    latestFeedbackAtMs,
    feedbackPendingRerun:
      latestFeedbackAtMs !== null && (latestOutputAtMs === null || latestFeedbackAtMs > latestOutputAtMs),
  };
};

export default function RFPDetailPage() {
  const [sourceOpen, setSourceOpen] = useState(false);
  const { rfp_id } = useParams();
  const navigate = useNavigate();
  const [rfp, setRfp] = useState<RFPDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [feedbackText, setFeedbackText] = useState("");
  const [submittingFeedback, setSubmittingFeedback] = useState(false);
  const [feedbackSubmittingStep, setFeedbackSubmittingStep] = useState<number | null>(null);
  const [feedbackStatusMessage, setFeedbackStatusMessage] = useState<string | null>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const [reflectionError, setReflectionError] = useState<string | null>(null);
  const [reflectionMessage, setReflectionMessage] = useState<string | null>(null);
  const [runningStepNumber, setRunningStepNumber] = useState<number | null>(null);
  const [rerunningFromStep, setRerunningFromStep] = useState<number | null>(null);
  const [acceptingStepNumber, setAcceptingStepNumber] = useState<number | null>(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [expandedTimelineEvents, setExpandedTimelineEvents] = useState<Record<string, boolean>>({});
  const [expandedTimelineRaw, setExpandedTimelineRaw] = useState<Record<string, boolean>>({});
  const [timelineOpen, setTimelineOpen] = useState(false);
  const devMode = useAppStore((state) => state.devMode);
  const setCurrentRfpTitle = useAppStore((state) => state.setCurrentRfpTitle);
  const [hoveredSentenceId, setHoveredSentenceId] = useState<string | null>(null);
  const [draftRange, setDraftRange] = useState<Step5DraftRange | null>(null);
  const [draftAnnotationText, setDraftAnnotationText] = useState("");
  const [editingAnnotationId, setEditingAnnotationId] = useState<string | null>(null);
  // Unsaved popup text per paragraph (keyed by paragraph text), restored when it is reopened.
  const [paragraphDrafts, setParagraphDrafts] = useState<Record<string, string>>({});
  const [rewriteVersions, setRewriteVersions] = useState<string[]>([]);
  const [rewritePart, setRewritePart] = useState<{ key: string; limit: number | null } | null>(null);
  const [selectedRewrite, setSelectedRewrite] = useState<number | null>(null);
  const [rewriteBusy, setRewriteBusy] = useState(false);
  const [applyingRewrite, setApplyingRewrite] = useState(false);
  const [rewriteError, setRewriteError] = useState<string | null>(null);
  const [step5Annotations, setStep5Annotations] = useState<Step5Annotation[]>([]);
  const [step4SelectedExpertIds, setStep4SelectedExpertIds] = useState<string[]>([]);
  const [step4ExpertSearchText, setStep4ExpertSearchText] = useState("");
  const [step4AcceptDialogOpen, setStep4AcceptDialogOpen] = useState(false);
  const [pendingAcceptRunId, setPendingAcceptRunId] = useState<number | null>(null);
  const [draggingProjectId, setDraggingProjectId] = useState<string | null>(null);
  const [dragOverProjectId, setDragOverProjectId] = useState<string | null>(null);
  const [reorderingPool, setReorderingPool] = useState(false);
  const [armedProjectId, setArmedProjectId] = useState<string | null>(null);
  // Applied to the rendered pool straight away; the server call catches up after.
  const [pendingPoolOrder, setPendingPoolOrder] = useState<string[] | null>(null);
  const [poolOrderSavedAt, setPoolOrderSavedAt] = useState<number | null>(null);
  const [feedbackExampleIndex, setFeedbackExampleIndex] = useState(0);
  const [draggingExpertId, setDraggingExpertId] = useState<string | null>(null);
  const [dragOverExpertId, setDragOverExpertId] = useState<string | null>(null);
  const [restoringRunId, setRestoringRunId] = useState<number | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [manualProjectTitle, setManualProjectTitle] = useState("");
  const [manualProjectText, setManualProjectText] = useState("");
  const [manualProjectSource, setManualProjectSource] = useState<"jyc" | "expert">("jyc");
  const [manualProjectSubmitting, setManualProjectSubmitting] = useState(false);
  const [manualProjectRemovingId, setManualProjectRemovingId] = useState<string | null>(null);
  const [editingProjectId, setEditingProjectId] = useState<string | null>(null);
  const [pitchEditing, setPitchEditing] = useState(false);
  const [pitchDrafts, setPitchDrafts] = useState<Record<string, string>>({});
  const [savingPitchEdit, setSavingPitchEdit] = useState(false);
  const [notesEditing, setNotesEditing] = useState(false);
  const [notesDraft, setNotesDraft] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [editProjectTitle, setEditProjectTitle] = useState("");
  const [editProjectText, setEditProjectText] = useState("");
  const [savingProjectEdit, setSavingProjectEdit] = useState(false);
  const [confirmRemoveProject, setConfirmRemoveProject] = useState<{ id: string; title: string } | null>(null);
  const initialStepSelectedRef = useRef(false);
  // Which rfp/step the server draft has been loaded for; autosave waits for it.
  const draftLoadedKeyRef = useRef<string | null>(null);

  const fetchRFPDetail = useCallback(async (options?: { silent?: boolean }): Promise<RFPDetail | null> => {
    const silent = Boolean(options?.silent);
    try {
      if (!silent) {
        setLoading(true);
      }
      const response = await authFetch(`/api/v1/rfps/${rfp_id}`);

      if (!response.ok) {
        throw new Error(`Failed to fetch RFP: ${response.statusText}`);
      }

      const data = await response.json();
      setRfp(data.data);
      setCurrentStep((prev: number) => {
        const maxStep = data.data.pipeline.total_steps || 1;
        const pipelineCurrentStep = data.data.pipeline.current_step || prev;
        const targetStep = initialStepSelectedRef.current ? prev : pipelineCurrentStep;
        initialStepSelectedRef.current = true;
        return Math.min(Math.max(targetStep, 1), maxStep);
      });
      setError(null);
      return data.data as RFPDetail;
    } catch (err) {
      setError((err as Error).message);
      console.error("Error fetching RFP detail:", err);
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [rfp_id]);

  useEffect(() => {
    if (rfp_id) {
      fetchRFPDetail();
    }
  }, [fetchRFPDetail, rfp_id]);

  // Feed the top bar the RFP's own name while this page is open.
  useEffect(() => {
    setCurrentRfpTitle(rfp?.title?.trim() || "");
  }, [rfp?.title, setCurrentRfpTitle]);

  useEffect(() => () => setCurrentRfpTitle(""), [setCurrentRfpTitle]);

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (rfp?.status === "processing") {
        void fetchRFPDetail({ silent: true });
      }
    }, 5000);
    return () => window.clearInterval(timer);
  }, [fetchRFPDetail, rfp?.status]);

  useEffect(() => {
    setExpandedTimelineEvents({});
    setExpandedTimelineRaw({});
    setTimelineOpen(false);
    setFeedbackStatusMessage(null);
  }, [currentStep]);

  const outputByStep = useMemo(() => {
    const map = new Map<number, RFPOutput>();
    (rfp?.outputs || []).forEach((o) => map.set(o.step_number, normalizeStepOutput(o) ?? o));
    return map;
  }, [rfp]);

  const { projectStep, finalStep } = useMemo(() => getReviewSteps(rfp?.pipeline_steps), [rfp]);
  const stepOutputForSelection = outputByStep.get(currentStep);
  const isStep5PitchOutput = currentStep === finalStep && isPitchOutput(stepOutputForSelection);

  useEffect(() => {
    if (currentStep !== projectStep || !stepOutputForSelection) {
      setStep4SelectedExpertIds([]);
      setStep4ExpertSearchText("");
      return;
    }
    const recommendation = (stepOutputForSelection.output.expert_recommendation ?? {}) as Record<string, unknown>;
    const selected = Array.isArray(recommendation.selected_experts)
      ? (recommendation.selected_experts as Array<Record<string, unknown>>)
      : [];
    setStep4SelectedExpertIds(selected.map((expert) => expertId(expert)).filter(Boolean));
  }, [currentStep, projectStep, stepOutputForSelection?.agent_slug, stepOutputForSelection?.created_at]);
  const step4AgentChoiceExpertIds = useMemo(() => {
    if (currentStep !== projectStep || !stepOutputForSelection) {
      return [];
    }
    const recommendation = (stepOutputForSelection.output.expert_recommendation ?? {}) as Record<string, unknown>;
    const selected = Array.isArray(recommendation.selected_experts)
      ? (recommendation.selected_experts as Array<Record<string, unknown>>)
      : [];
    if (selected.length > 0) {
      return selected.map((expert) => expertId(expert)).filter(Boolean);
    }
    const recommended = Array.isArray(recommendation.recommended_experts)
      ? (recommendation.recommended_experts as Array<Record<string, unknown>>)
      : [];
    return recommended.map((expert) => expertId(expert)).filter(Boolean);
  }, [currentStep, projectStep, stepOutputForSelection?.agent_slug, stepOutputForSelection?.created_at]);
  const step5ReviewModel = useMemo(
    () => buildStep5ReviewModel(isStep5PitchOutput ? stepOutputForSelection : undefined),
    [isStep5PitchOutput, stepOutputForSelection]
  );
  const annotationFeedbackReady = useMemo(
    () => step5Annotations.length > 0 && step5Annotations.every((item) => item.feedback_text.trim().length > 0),
    [step5Annotations]
  );
  const step5ParagraphsBySectionKey = useMemo(
    () => new Map(step5ReviewModel.sections.map((section) => [section.section_key, section.sentences])),
    [step5ReviewModel.sections]
  );
  const persistedParagraphIds = useMemo(
    () => new Set(step5Annotations.flatMap((annotation) => annotation.sentence_ids)),
    [step5Annotations]
  );
  const draftParagraphIds = useMemo(
    () =>
      new Set([
        ...(draftRange?.sentence_ids ?? []),
        ...step5ReviewModel.sentences
          .filter((paragraph) => paragraphDrafts[paragraph.sentence_text])
          .map((paragraph) => paragraph.id),
      ]),
    [draftRange, step5ReviewModel.sentences, paragraphDrafts]
  );

  useEffect(() => {
    setHoveredSentenceId(null);
    setDraftRange(null);
    setDraftAnnotationText("");
    setEditingAnnotationId(null);
    setStep5Annotations([]);
  }, [currentStep, stepOutputForSelection?.agent_slug, stepOutputForSelection?.created_at]);

  // Cycle the ghost example while the box is still empty.
  useEffect(() => {
    setFeedbackExampleIndex(0);
  }, [currentStep]);

  useEffect(() => {
    if (feedbackText.trim()) return;
    const timer = window.setInterval(() => setFeedbackExampleIndex((prev) => prev + 1), 4000);
    return () => window.clearInterval(timer);
  }, [feedbackText, currentStep]);

  useEffect(() => {
    if (poolOrderSavedAt === null) return;
    const timer = window.setTimeout(() => setPoolOrderSavedAt(null), 2500);
    return () => window.clearTimeout(timer);
  }, [poolOrderSavedAt]);

  // Unsubmitted feedback lives on the server, so a refresh or another device keeps it.
  useEffect(() => {
    if (!rfp_id) return;
    const stepKey = `${rfp_id}:${currentStep}`;
    let cancelled = false;
    draftLoadedKeyRef.current = null;
    void (async () => {
      try {
        const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${currentStep}/draft`);
        if (cancelled) return;
        if (response.ok) {
          const payload = await response.json().catch(() => null);
          const draft = (payload?.draft || {}) as { feedback_text?: string; annotations?: Step5Annotation[] };
          if (typeof draft.feedback_text === "string") {
            setFeedbackText(draft.feedback_text);
          }
          if (Array.isArray(draft.annotations)) {
            setStep5Annotations(draft.annotations);
          }
        }
      } catch {
        // A missing draft must never block the step UI.
      } finally {
        if (!cancelled) {
          draftLoadedKeyRef.current = stepKey;
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rfp_id, currentStep]);

  useEffect(() => {
    if (!rfp_id) return;
    const stepKey = `${rfp_id}:${currentStep}`;
    if (draftLoadedKeyRef.current !== stepKey) return;
    if (submittingFeedback) return;
    const hasDraft = Boolean(feedbackText.trim()) || step5Annotations.length > 0;
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          if (hasDraft) {
            await authFetch(`/api/v1/rfps/${rfp_id}/step/${currentStep}/draft`, {
              method: "PUT",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ feedback_text: feedbackText, annotations: step5Annotations }),
            });
          } else {
            await authFetch(`/api/v1/rfps/${rfp_id}/step/${currentStep}/draft`, { method: "DELETE" });
          }
        } catch {
          // Draft saving is best effort; the reviewer's text stays in the box either way.
        }
      })();
    }, 800);
    return () => window.clearTimeout(timer);
  }, [rfp_id, currentStep, feedbackText, step5Annotations, submittingFeedback]);

  const rerunRequiredFromStep = useMemo(() => {
    if (!rfp) return null;

    for (let step = 1; step <= rfp.pipeline.total_steps; step += 1) {
      const output = outputByStep.get(step);
      if (!output || !output.feedback || output.feedback.length === 0) {
        continue;
      }

      const outputTime = new Date(output.created_at).getTime();
      const latestFeedbackTime = Math.max(
        ...output.feedback.map((f) => new Date(f.created_at).getTime())
      );

      if (latestFeedbackTime > outputTime) {
        return step + 1 <= rfp.pipeline.total_steps ? step + 1 : null;
      }
    }

    return null;
  }, [rfp, outputByStep]);

  const stepVersionByStep = useMemo(() => {
    const map = new Map<number, StepVersionStats>();
    if (!rfp) return map;
    rfp.pipeline_steps.forEach((step) => {
      const timeline = rfp.timeline_by_step?.[String(step.step)];
      map.set(step.step, getStepVersionStats(timeline));
    });
    return map;
  }, [rfp]);

  const activeBackendStepNumber = useMemo(
    () =>
      rfp?.active_jobs
        ?.filter((job) => ACTIVE_JOB_STATUSES.has(String(job.status || "").toLowerCase()))
        .map((job) => job.step_number)
        .find((stepNumber): stepNumber is number => typeof stepNumber === "number") ?? null,
    [rfp]
  );

  const getStepState = (step: number): StepUiState => {
    if (!rfp) return "not_run";

    const output = outputByStep.get(step);
    const previousOutput = step > 1 ? outputByStep.get(step - 1) : null;

    if (
      runningStepNumber === step ||
      feedbackSubmittingStep === step ||
      rerunningFromStep === step ||
      activeBackendStepNumber === step
    ) {
      return "running";
    }

    if (
      rfp.status === "processing" &&
      rfp.pipeline.status !== "completed" &&
      rfp.pipeline.current_step === step &&
      !output
    ) {
      return "running";
    }

    if (rerunRequiredFromStep !== null && step >= rerunRequiredFromStep) {
      return "rerun_required";
    }

    if (step > 1 && !previousOutput) {
      return "blocked";
    }

    if (step === finalStep) {
      const projectTimeline = rfp.timeline_by_step?.[String(projectStep)];
      if (!projectTimeline?.accepted_output_run_id) {
        return "blocked";
      }
    }

    if (output) {
      const runStatus = String(output.run_status || "").trim().toLowerCase();
      if (HOLD_RUN_STATUSES.has(runStatus)) {
        return "awaiting_input";
      }
      return "completed";
    }

    if (rfp.pipeline.current_step === step && rfp.status !== "completed") {
      return "current_head";
    }

    return "not_run";
  };

  const handleStep5SentenceClick = (sentenceId: string) => {
    if (!isStep5PitchOutput || submittingFeedback) return;
    if (draftRange) {
      setFeedbackError("Save or cancel the current annotation before selecting another paragraph.");
      return;
    }
    const paragraph = step5ReviewModel.sentences.find((item) => item.id === sentenceId);
    if (!paragraph) {
      setFeedbackError("Unable to resolve selected paragraph.");
      return;
    }
    if (overlapsExistingAnnotation(step5Annotations, [paragraph.id])) {
      setFeedbackError("This paragraph already has an annotation. Edit or delete the existing one first.");
      return;
    }

    const nextRange: Step5DraftRange = {
      section_key: paragraph.section_key,
      section_label: paragraph.section_label,
      start_sentence_index: paragraph.sentence_index,
      end_sentence_index: paragraph.sentence_index,
      selected_sentences: [
        {
          section_key: paragraph.section_key,
          section_label: paragraph.section_label,
          sentence_index: paragraph.sentence_index,
          sentence_text: paragraph.sentence_text,
        },
      ],
      sentence_ids: [paragraph.id],
      global_start: paragraph.global_index,
      global_end: paragraph.global_index,
      excerpt_text: paragraph.sentence_text,
    };

    setDraftRange(nextRange);
    setDraftAnnotationText(paragraphDrafts[paragraph.sentence_text] || "");
    setEditingAnnotationId(null);
    setFeedbackError(null);
    resetRewrite();
  };

  const resetRewrite = () => {
    setRewriteVersions([]);
    setRewritePart(null);
    setSelectedRewrite(null);
    setRewriteError(null);
  };

  const forgetParagraphDraft = (paragraphText: string) =>
    setParagraphDrafts((prev) => {
      if (!(paragraphText in prev)) return prev;
      const next = { ...prev };
      delete next[paragraphText];
      return next;
    });

  const handleSaveDraftAnnotation = () => {
    if (!draftRange) return;
    const text = draftAnnotationText.trim();
    if (!text) {
      setFeedbackError("Please enter feedback for the selected paragraph.");
      return;
    }

    if (editingAnnotationId) {
      setStep5Annotations((prev) =>
        prev.map((item) =>
          item.annotation_id === editingAnnotationId ? { ...item, ...draftRange, feedback_text: text } : item
        )
      );
    } else {
      const annotationId = `ann-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const nextAnnotation: Step5Annotation = {
        ...draftRange,
        annotation_id: annotationId,
        feedback_text: text,
      };
      setStep5Annotations((prev) => [...prev, nextAnnotation]);
    }

    forgetParagraphDraft(draftRange.excerpt_text);
    resetRewrite();
    setDraftRange(null);
    setDraftAnnotationText("");
    setEditingAnnotationId(null);
    setFeedbackError(null);
  };

  const handleEditAnnotation = (annotationId: string) => {
    const existing = step5Annotations.find((item) => item.annotation_id === annotationId);
    if (!existing) return;
    setDraftRange({
      section_key: existing.section_key,
      section_label: existing.section_label,
      start_sentence_index: existing.start_sentence_index,
      end_sentence_index: existing.end_sentence_index,
      selected_sentences: existing.selected_sentences,
      sentence_ids: existing.sentence_ids,
      global_start: existing.global_start,
      global_end: existing.global_end,
      excerpt_text: existing.excerpt_text,
    });
    setDraftAnnotationText(paragraphDrafts[existing.excerpt_text] ?? existing.feedback_text);
    setEditingAnnotationId(existing.annotation_id);
    setFeedbackError(null);
    resetRewrite();
  };

  const handleDeleteAnnotation = (annotationId: string) => {
    setStep5Annotations((prev) => prev.filter((item) => item.annotation_id !== annotationId));
    if (editingAnnotationId === annotationId) {
      setDraftRange(null);
      setDraftAnnotationText("");
      setEditingAnnotationId(null);
    }
  };

  const closeDraftAnnotation = () => {
    setDraftRange(null);
    setDraftAnnotationText("");
    setEditingAnnotationId(null);
    setFeedbackError(null);
    resetRewrite();
  };

  // Cancel throws the typed text away.
  const handleCancelDraftAnnotation = () => {
    if (draftRange) forgetParagraphDraft(draftRange.excerpt_text);
    closeDraftAnnotation();
  };

  // Clicking outside or Esc keeps the typed text as a draft for that paragraph; only Save adds it to feedback.
  const handleDismissDraftAnnotation = () => {
    if (!draftRange) return;
    const text = draftAnnotationText.trim();
    const saved = step5Annotations.find((item) => item.annotation_id === editingAnnotationId)?.feedback_text;
    if (text && text !== saved) {
      setParagraphDrafts((prev) => ({ ...prev, [draftRange.excerpt_text]: draftAnnotationText }));
    } else {
      forgetParagraphDraft(draftRange.excerpt_text);
    }
    closeDraftAnnotation();
  };

  const handleRunParagraphRewrite = async () => {
    if (!draftRange || finalStep === null || rewriteBusy) return;
    const feedback = draftAnnotationText.trim();
    if (!feedback) return;
    try {
      setRewriteBusy(true);
      setRewriteError(null);
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${finalStep}/paragraph-rewrite`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paragraph_text: draftRange.excerpt_text,
          feedback,
          previous_attempts: rewriteVersions,
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to rewrite the paragraph");
      }
      const text = String(payload?.rewritten_text || "").trim();
      if (!text) throw new Error("The rewrite came back empty");
      setRewritePart({ key: String(payload.part_key), limit: payload.part_limit ? Number(payload.part_limit) : null });
      setRewriteVersions((prev) => [...prev, text]);
      setSelectedRewrite(rewriteVersions.length);
    } catch (err) {
      setRewriteError((err as Error).message);
    } finally {
      setRewriteBusy(false);
    }
  };

  const handleApplyParagraphRewrite = async () => {
    if (!draftRange || !rewritePart || selectedRewrite === null || finalStep === null || !currentOutput) return;
    const section = (currentOutput.output[rewritePart.key] as Record<string, unknown> | undefined) ?? {};
    const paragraphs = splitIntoParagraphs(String(section.content || ""));
    const index = paragraphs.indexOf(draftRange.excerpt_text);
    if (index < 0) {
      setRewriteError("This paragraph changed since the popup opened. Close it and try again.");
      return;
    }
    paragraphs[index] = rewriteVersions[selectedRewrite];
    try {
      setApplyingRewrite(true);
      setRewriteError(null);
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${finalStep}/output`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ [rewritePart.key]: paragraphs.join("\n\n") }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to save the new paragraph");
      }
      const warnings = Array.isArray(payload?.warnings) ? (payload.warnings as string[]) : [];
      forgetParagraphDraft(draftRange.excerpt_text);
      closeDraftAnnotation();
      setFeedbackStatusMessage(
        warnings.length > 0
          ? `Paragraph updated as a new version. Over the limit: ${warnings.join(" ")}`
          : "Paragraph updated as a new version."
      );
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setRewriteError((err as Error).message);
    } finally {
      setApplyingRewrite(false);
    }
  };

  const handleSubmitFeedback = async () => {
    if (!rfp) {
      return;
    }

    const requestBody: Record<string, unknown> = { provided_by: "user" };
    if (isStep5PitchOutput && stepOutputForSelection) {
      if (!annotationFeedbackReady) {
        setFeedbackError(`Add at least one annotated paragraph with feedback before sending Step ${finalStep} feedback.`);
        return;
      }
      const summaryLines = step5Annotations.map(
        (annotation, idx) =>
          `${idx + 1}. ${annotation.section_label} (${annotation.section_key} paragraph ${annotation.start_sentence_index}): ${annotation.feedback_text}`
      );
      requestBody.feedback_text = `Apply the following targeted revisions:\n${summaryLines.join("\n")}`;
      requestBody.targeted_feedback = {
        mode: "annotated_ranges",
        source_agent_slug: stepOutputForSelection.agent_slug,
        source_step: currentStep,
        annotations: step5Annotations.map((annotation) => ({
          annotation_id: annotation.annotation_id,
          section_key: annotation.section_key,
          section_label: annotation.section_label,
          start_sentence_index: annotation.start_sentence_index,
          end_sentence_index: annotation.end_sentence_index,
          excerpt_text: annotation.excerpt_text,
          feedback_text: annotation.feedback_text,
          selected_sentences: annotation.selected_sentences,
        })),
      };
    } else {
      if (!feedbackText.trim()) {
        return;
      }
      requestBody.feedback_text = feedbackText;
    }

    try {
      setSubmittingFeedback(true);
      setFeedbackSubmittingStep(currentStep);
      setFeedbackStatusMessage(`Submitting feedback for Step ${currentStep}...`);
      setFeedbackError(null);

      const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${currentStep}/feedback`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "Failed to submit feedback");
      }
      const feedbackPayload = await response.json().catch(() => null);

      setFeedbackText("");
      if (isStep5PitchOutput) {
        setStep5Annotations([]);
        setDraftRange(null);
        setDraftAnnotationText("");
        setEditingAnnotationId(null);
      }
      setFeedbackStatusMessage(
        typeof feedbackPayload?.message === "string"
          ? feedbackPayload.message
          : `Feedback saved for Step ${currentStep}. Processing in background.`
      );
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setFeedbackError((err as Error).message);
    } finally {
      setSubmittingFeedback(false);
      setFeedbackSubmittingStep(null);
    }
  };

  const handleRunStep = async (step: number) => {
    try {
      setRunError(null);
      setRunningStepNumber(step);
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${step}/run`, {
        method: "POST",
      });

      if (!response.ok) {
        const errPayload = await response.json().catch(() => null);
        throw new Error(errPayload?.detail || "Failed to run step");
      }

      const payload = await response.json().catch(() => null);
      setFeedbackStatusMessage(
        typeof payload?.message === "string"
          ? payload.message
          : `Step ${step} queued and running in background.`
      );
      await fetchRFPDetail({ silent: true });
      setCurrentStep(step);
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setRunningStepNumber(null);
    }
  };

  const handleRerunFrom = async (step: number) => {
    try {
      setRunError(null);
      setRerunningFromStep(step);
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/rerun-from-step/${step}`, {
        method: "POST",
      });

      if (!response.ok) {
        const errPayload = await response.json().catch(() => null);
        throw new Error(errPayload?.detail || "Failed to reset downstream steps");
      }

      await fetchRFPDetail();
      setCurrentStep(step);
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setRerunningFromStep(null);
    }
  };

  const handleAcceptAll = async () => {
    if (!rfp) return;

    try {
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/accept-all`, {
        method: "POST",
      });

      if (!response.ok) {
        const payload = await response.json().catch(() => null);
        throw new Error(payload?.detail || "Failed to accept all steps");
      }

      const payload = await response.json().catch(() => null);
      setReflectionMessage(
        typeof payload?.message === "string"
          ? payload.message
          : "Accept-all queued. Finalization is running in background."
      );
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setError((err as Error).message);
    }
  };

  const submitAcceptStep = async (step: number, selectedExpertIdsOverride?: string[], runId?: number | null) => {
    try {
      setRunError(null);
      setReflectionError(null);
      setReflectionMessage(null);
      setAcceptingStepNumber(step);
      const selectedExpertIds = selectedExpertIdsOverride ?? step4SelectedExpertIds;
      const acceptBody: Record<string, unknown> = step === projectStep ? { selected_expert_ids: selectedExpertIds } : {};
      if (typeof runId === "number") {
        acceptBody.run_id = runId;
      }

      const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${step}/accept`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(acceptBody),
      });

      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to accept step output");
      }

      setReflectionMessage(
        typeof payload?.message === "string"
          ? payload.message
          : `Step ${step} acceptance queued.`
      );
      if (step === projectStep) {
        setFeedbackStatusMessage(`Step ${projectStep} approved. Step ${finalStep} is now running in the background.`);
        setCurrentStep(finalStep);
      }
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setAcceptingStepNumber(null);
    }
  };

  const handleAcceptStep = (step: number, runId?: number | null) => {
    // Umbrex experts are opt-in, so approving carries whatever the reviewer added
    // (usually nothing) instead of asking about an agent recommendation there isn't one of.
    if (step === projectStep && rfp?.platform_type === "Umbrex") {
      void submitAcceptStep(step, step4SelectedExpertIds, runId ?? null);
      return;
    }
    if (step === projectStep) {
      setPendingAcceptRunId(typeof runId === "number" ? runId : null);
      setStep4AcceptDialogOpen(true);
      return;
    }
    void submitAcceptStep(step, undefined, runId ?? null);
  };

  const handleConfirmStep4Experts = (includeExperts: boolean) => {
    setStep4AcceptDialogOpen(false);
    const runId = pendingAcceptRunId;
    setPendingAcceptRunId(null);
    void submitAcceptStep(projectStep, includeExperts ? step4SelectedExpertIds : [], runId);
  };

  const poolProjectIds = (): string[] => {
    const pool = outputByStep.get(projectStep)?.output?.selected_projects;
    if (!Array.isArray(pool)) return [];
    return pool
      .map((item) => (item && typeof item === "object" ? String((item as Record<string, unknown>).project_id || "") : ""))
      .filter(Boolean);
  };

  const submitPoolOrder = async (projectIds: string[]) => {
    try {
      setRunError(null);
      // Move the cards now; a failure puts the server's order back.
      setPendingPoolOrder(projectIds);
      setPoolOrderSavedAt(null);
      setReorderingPool(true);
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${projectStep}/projects-order`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ project_ids: projectIds }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to reorder the project pool");
      }
      await fetchRFPDetail({ silent: true });
      setPoolOrderSavedAt(Date.now());
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setPendingPoolOrder(null);
      setReorderingPool(false);
    }
  };

  const handleReorderPool = async (sourceId: string, targetId: string) => {
    const ids = pendingPoolOrder ?? poolProjectIds();
    const from = ids.indexOf(sourceId);
    const to = ids.indexOf(targetId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, sourceId);
    await submitPoolOrder(next);
  };

  const handleMoveProject = async (projectId: string, direction: -1 | 1) => {
    const ids = pendingPoolOrder ?? poolProjectIds();
    const from = ids.indexOf(projectId);
    const to = from + direction;
    if (from < 0 || to < 0 || to >= ids.length) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, projectId);
    await submitPoolOrder(next);
  };

  const handleRestoreVersion = async (runId: number) => {
    try {
      setRunError(null);
      setReflectionError(null);
      setRestoringRunId(runId);
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${currentStep}/restore/${runId}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to restore this version");
      }
      setReflectionMessage(
        typeof payload?.message === "string" ? payload.message : "Version restored as the latest version."
      );
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setRestoringRunId(null);
    }
  };

  const handleAddManualProject = async () => {
    const text = manualProjectText.trim();
    if (!text || manualProjectSubmitting) return;
    try {
      setManualProjectSubmitting(true);
      setRunError(null);
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${projectStep}/manual-projects`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          projects: [{ title: manualProjectTitle.trim(), text, source: manualProjectSource }],
        }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to add manual project");
      }
      setManualProjectTitle("");
      setManualProjectText("");
      setFeedbackStatusMessage(`Project added to the Step ${projectStep} pool.`);
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setManualProjectSubmitting(false);
    }
  };

  const handleRemoveManualProject = async (projectId: string) => {
    if (!projectId || manualProjectRemovingId) return;
    try {
      setManualProjectRemovingId(projectId);
      setRunError(null);
      const response = await authFetch(
        `/api/v1/rfps/${rfp_id}/step/${projectStep}/manual-projects/${encodeURIComponent(projectId)}`,
        { method: "DELETE" }
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to remove manual project");
      }
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setManualProjectRemovingId(null);
    }
  };

  const handleSaveProjectEdit = async () => {
    const projectId = editingProjectId;
    const text = editProjectText.trim();
    if (!projectId || !text || savingProjectEdit) return;
    try {
      setSavingProjectEdit(true);
      setRunError(null);
      const response = await authFetch(
        `/api/v1/rfps/${rfp_id}/step/${projectStep}/projects/${encodeURIComponent(projectId)}`,
        {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ title: editProjectTitle.trim(), text }),
        }
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to update project");
      }
      setEditingProjectId(null);
      setFeedbackStatusMessage(`Project updated in the Step ${projectStep} pool.`);
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setSavingProjectEdit(false);
    }
  };

  const handleSavePitchEdit = async () => {
    if (savingPitchEdit || finalStep === null) return;
    try {
      setSavingPitchEdit(true);
      setRunError(null);
      const body = Object.fromEntries(
        STANDARD_PITCH_PART_DEFS.map((part) => [part.key, (pitchDrafts[part.key] ?? "").trim()])
      );
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/step/${finalStep}/output`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to save the edited text");
      }
      const warnings = Array.isArray(payload?.warnings) ? (payload.warnings as string[]) : [];
      setPitchEditing(false);
      setFeedbackStatusMessage(
        warnings.length > 0
          ? `Edited text saved as a new version. Over the limit: ${warnings.join(" ")}`
          : "Edited text saved as a new version."
      );
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setSavingPitchEdit(false);
    }
  };

  // Saving notes changes nothing that already ran, so name the steps whose output predates them.
  const stepsBehindNotes = (() => {
    if (!rfp || rfp.platform_type !== "Umbrex") return [] as number[];
    if (!rfp.bidder_notes?.trim() || !rfp.bidder_notes_updated_at) return [] as number[];
    const notesAt = Date.parse(rfp.bidder_notes_updated_at);
    if (Number.isNaN(notesAt)) return [] as number[];
    return (rfp.outputs || [])
      .filter((output) => {
        const outputAt = Date.parse(output.created_at);
        return !Number.isNaN(outputAt) && outputAt < notesAt;
      })
      .map((output) => output.step_number)
      .sort((left, right) => left - right);
  })();

  const handleSaveNotes = async () => {
    if (savingNotes) return;
    try {
      setSavingNotes(true);
      setRunError(null);
      const response = await authFetch(`/api/v1/rfps/${rfp_id}/bidder-notes`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ bidder_notes: notesDraft.trim() }),
      });
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to save notes");
      }
      setNotesEditing(false);
      setFeedbackStatusMessage("DC notes saved. Steps you run or re-run from now on will use them.");
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setSavingNotes(false);
    }
  };

  const handleRemovePoolProject = async (projectId: string) => {
    if (!projectId || manualProjectRemovingId) return;
    try {
      setManualProjectRemovingId(projectId);
      setRunError(null);
      const response = await authFetch(
        `/api/v1/rfps/${rfp_id}/step/${projectStep}/projects/${encodeURIComponent(projectId)}`,
        { method: "DELETE" }
      );
      const payload = await response.json().catch(() => null);
      if (!response.ok) {
        throw new Error(payload?.detail || "Failed to remove project");
      }
      setFeedbackStatusMessage(`Project removed from the Step ${projectStep} pool.`);
      await fetchRFPDetail({ silent: true });
    } catch (err) {
      setRunError((err as Error).message);
    } finally {
      setManualProjectRemovingId(null);
    }
  };

  const toggleTimelineEvent = (eventKey: string) => {
    setExpandedTimelineEvents((prev) => ({
      ...prev,
      [eventKey]: !prev[eventKey],
    }));
  };

  const toggleTimelineRaw = (eventKey: string) => {
    setExpandedTimelineRaw((prev) => ({
      ...prev,
      [eventKey]: !prev[eventKey],
    }));
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="h-9 w-48 bp-skeleton" />
        <div className="h-36 w-full rounded-2xl bp-skeleton" />
        <div className="h-48 w-full rounded-2xl bp-skeleton" />
        <p className="text-center text-sm text-slate-500">Loading RFP details...</p>
      </div>
    );
  }

  if (!rfp) {
    return (
      <Card className="mx-auto max-w-md p-12 text-center">
        <p className="text-lg font-semibold text-slate-900">RFP not found</p>
        <p className="mt-1 text-sm text-slate-500">It may have been deleted, or the link is wrong.</p>
        <Button onClick={() => navigate("/")} className="mt-5">
          Back to Home
        </Button>
      </Card>
    );
  }

  const currentStepState = getStepState(currentStep);
  const currentOutput = stepOutputForSelection;

  // While a reorder is in flight the cards show the new order, not the stored one.
  const currentOutputForDisplay = (() => {
    if (!currentOutput || !pendingPoolOrder || currentStep !== projectStep) return currentOutput;
    const pool = currentOutput.output?.selected_projects;
    if (!Array.isArray(pool)) return currentOutput;
    const byId = new Map(
      pool.map((item) => [
        item && typeof item === "object" ? String((item as Record<string, unknown>).project_id || "") : "",
        item,
      ])
    );
    const ordered = pendingPoolOrder.map((id) => byId.get(id)).filter(Boolean);
    if (ordered.length !== pool.length) return currentOutput;
    return {
      ...currentOutput,
      output: {
        ...currentOutput.output,
        selected_projects: ordered.map((item, index) => ({
          ...(item as Record<string, unknown>),
          rank: index + 1,
        })),
      },
    };
  })();
  const allStepsFinal =
    rfp.pipeline_steps.length > 0 &&
    rfp.pipeline_steps.every((step) => outputByStep.get(step.step)?.run_status === "final");
  const timelineForStep = rfp.timeline_by_step?.[String(currentStep)] || {
    events: [],
    accepted_output: null,
    first_output_run_id: null,
    accepted_output_run_id: null,
  };
  const currentPipelineStep = rfp.pipeline_steps.find((step) => step.step === currentStep);
  const feedbackExamples = feedbackExamplesForRole(currentPipelineStep?.role);
  const feedbackPlaceholder = `e.g. ${feedbackExamples[feedbackExampleIndex % feedbackExamples.length]}`;
  const currentStepVersion = stepVersionByStep.get(currentStep) || emptyStepVersionStats;
  const latestRunId = currentStepVersion.latestOutputRunId;
  const acceptedRunId = timelineForStep.accepted_output_run_id;
  const currentStepVersionEntries = getStepVersionEntries(timelineForStep);
  const acceptedVersionNumber =
    currentStepVersionEntries.find((entry) => entry.runId === acceptedRunId)?.version ?? null;
  const hasFinalOutput = Boolean(currentOutput && String(currentOutput.run_status || "").toLowerCase() === "final");
  const hasPendingApproval =
    hasFinalOutput && latestRunId !== null && (acceptedRunId === null || latestRunId !== acceptedRunId);
  const stepAccepted = Boolean(acceptedRunId && latestRunId !== null && acceptedRunId === latestRunId);
  const step4Timeline = rfp.timeline_by_step?.[String(projectStep)];
  const step5Output = outputByStep.get(finalStep);
  const step5RunningAfterStep4Approval =
    Boolean(step4Timeline?.accepted_output_run_id) &&
    (!step5Output || String(step5Output.run_status || "").toLowerCase() !== "final");
  const backendRunningStepNumber =
    activeBackendStepNumber ??
    (rfp.status === "processing" &&
    rfp.pipeline.status !== "completed" &&
    !outputByStep.get(rfp.pipeline.current_step)
      ? rfp.pipeline.current_step
      : null);
  const rfpBusyStepNumber =
    runningStepNumber ??
    feedbackSubmittingStep ??
    rerunningFromStep ??
    (step5RunningAfterStep4Approval ? finalStep : backendRunningStepNumber);
  const currentStepBusy = rfpBusyStepNumber === currentStep;
  const catalantStep4Agent = rfp.pipeline_steps.find((step) => step.step === projectStep)?.agent_slug;
  const finalStepAgent = rfp.pipeline_steps.find((step) => step.step === finalStep)?.agent_slug;
  const catalantBranchLabel =
    rfp.platform_type === "Catalant"
      ? catalantStep4Agent === "catalant-idb-mapping"
        ? "Branch: IDB Mapping Path"
        : "Branch: Project Selector Path"
      : rfp.platform_type === "Umbrex"
        ? finalStepAgent === "umbrex-team-proposal"
          ? "Branch: Team Proposal"
          : "Branch: Standard Proposal"
        : null;
  const manualProjectOptions: ManualProjectPanelOptions | undefined =
    currentStep === projectStep && currentOutput && PROJECT_SELECTOR_AGENT_SLUGS.has(currentOutput.agent_slug)
      ? {
          title: manualProjectTitle,
          text: manualProjectText,
          source: manualProjectSource,
          submitting: manualProjectSubmitting,
          removingProjectId: manualProjectRemovingId,
          disabled: acceptingStepNumber === projectStep || currentStepBusy,
          onTitleChange: setManualProjectTitle,
          onTextChange: setManualProjectText,
          onSourceChange: setManualProjectSource,
          onAdd: () => {
            void handleAddManualProject();
          },
          onRemove: (projectId: string, isManual: boolean, title: string) => {
            if (isManual) {
              void handleRemoveManualProject(projectId);
            } else {
              setConfirmRemoveProject({ id: projectId, title });
            }
          },
          editingProjectId,
          editTitle: editProjectTitle,
          editText: editProjectText,
          savingEdit: savingProjectEdit,
          onStartEdit: (projectId: string, title: string, text: string) => {
            setEditingProjectId(projectId);
            setEditProjectTitle(title);
            setEditProjectText(text);
          },
          onEditTitleChange: setEditProjectTitle,
          onEditTextChange: setEditProjectText,
          onCancelEdit: () => setEditingProjectId(null),
          onSaveEdit: () => {
            void handleSaveProjectEdit();
          },
          reordering: reorderingPool,
          orderSavedAt: poolOrderSavedAt,
          armedProjectId,
          draggingProjectId,
          dragOverProjectId,
          onArmDrag: (projectId: string | null) => setArmedProjectId(projectId),
          onDragStartProject: (projectId: string) => setDraggingProjectId(projectId),
          onDragOverProject: (projectId: string) => setDragOverProjectId(projectId),
          onDropProject: (projectId: string) => {
            const sourceId = draggingProjectId;
            setDraggingProjectId(null);
            setDragOverProjectId(null);
            setArmedProjectId(null);
            if (sourceId && sourceId !== projectId) {
              void handleReorderPool(sourceId, projectId);
            }
          },
          onDragEndProject: () => {
            setDraggingProjectId(null);
            setDragOverProjectId(null);
            setArmedProjectId(null);
          },
          onMoveProject: (projectId: string, direction: -1 | 1) => {
            void handleMoveProject(projectId, direction);
          },
        }
      : undefined;

  const pitchEditOptions: PitchEditOptions | undefined =
    finalStep !== null &&
    currentStep === finalStep &&
    currentOutput &&
    THREE_PART_PITCH_AGENT_SLUGS.has(currentOutput.agent_slug) &&
    String(currentOutput.run_status || "").toLowerCase() === "final"
      ? {
          editing: pitchEditing,
          drafts: pitchDrafts,
          saving: savingPitchEdit,
          disabled: currentStepBusy || submittingFeedback,
          partLimits: isUmbrexSlug(currentOutput.agent_slug) ? UMBREX_PART_LIMITS : {},
          totalLimit: isUmbrexSlug(currentOutput.agent_slug) ? null : CATALANT_PITCH_TOTAL_LIMIT,
          onStart: () => {
            const output = currentOutput.output;
            setPitchDrafts(
              Object.fromEntries(
                STANDARD_PITCH_PART_DEFS.map((part) => {
                  const section = (output[part.key] as Record<string, unknown> | undefined) ?? {};
                  return [part.key, String(section.content || "")];
                })
              )
            );
            setPitchEditing(true);
          },
          onChange: (partKey: string, value: string) => setPitchDrafts((prev) => ({ ...prev, [partKey]: value })),
          onCancel: () => setPitchEditing(false),
          onSave: () => {
            void handleSavePitchEdit();
          },
        }
      : undefined;

  // On-the-spot paragraph rewrites save through the hand-edit route, so they need the same output kinds.
  const canRewriteParagraph = Boolean(pitchEditOptions) && !pitchEditing;
  const popupBusy = submittingFeedback || rewriteBusy || applyingRewrite;

  const getStepGuidance = (): string => {
    if (feedbackSubmittingStep === currentStep) {
      return "Feedback has been submitted. Waiting for the updated output version.";
    }
    if (currentStepVersion.feedbackPendingRerun) {
      return "Feedback is newer than the visible output. A rerun is pending or still processing.";
    }
    if (currentStepState === "blocked") {
      return "This step is blocked. Run the previous step first.";
    }
    if (currentStepState === "awaiting_input") {
      return "This step requires human feedback before the pipeline can continue.";
    }
    if (currentStepState === "rerun_required") {
      return `Upstream feedback changed. Reset and rerun from Step ${rerunRequiredFromStep}.`;
    }
    if (!currentOutput) {
      return "Run this step to generate output.";
    }
    if (allStepsFinal && rfp.status !== "completed") {
      return "All steps are final. Finalize the RFP when ready.";
    }
    if (hasPendingApproval) {
      return "A new output version is ready. Approve this version to lock it in.";
    }
    if (stepAccepted) {
      return "Current output version is already approved. Provide feedback or rerun to create a new version.";
    }
    return "Step accepted. Use advanced actions if you need to rerun or reflect.";
  };

  const getPrimaryAction = (): { label: string; disabled: boolean; onClick: () => void } => {
    if (currentStepState === "rerun_required" && rerunRequiredFromStep !== null) {
      return {
        label:
          rerunningFromStep === rerunRequiredFromStep
            ? "Resetting..."
            : `Reset and Rerun from Step ${rerunRequiredFromStep}`,
        disabled: rerunningFromStep === rerunRequiredFromStep,
        onClick: () => handleRerunFrom(rerunRequiredFromStep),
      };
    }

    if (currentStepState === "awaiting_input") {
      const awaitingInputReady = isStep5PitchOutput ? annotationFeedbackReady : Boolean(feedbackText.trim());
      return {
        label: submittingFeedback
          ? isStep5PitchOutput
            ? `Rerunning Step ${finalStep}...`
            : "Submitting..."
          : `Submit Feedback for Step ${currentStep}`,
        disabled: submittingFeedback || !awaitingInputReady,
        onClick: handleSubmitFeedback,
      };
    }

    if (allStepsFinal && rfp.status !== "completed") {
      return {
        label: "Accept All and Finalize",
        disabled: false,
        onClick: handleAcceptAll,
      };
    }

    if (currentOutput && hasPendingApproval) {
      return {
        label: acceptingStepNumber === currentStep ? "Accepting..." : `Approve Step ${currentStep}`,
        disabled: acceptingStepNumber === currentStep,
        onClick: () => handleAcceptStep(currentStep),
      };
    }

    return {
      label: currentStepBusy ? "Running..." : `Re-run Step ${currentStep}`,
      disabled: currentStepState === "blocked" || currentStepState === "running" || currentStepBusy,
      onClick: () => handleRunStep(currentStep),
    };
  };

  const primaryAction = getPrimaryAction();

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2.5">
          <button
            type="button"
            onClick={() => navigate("/")}
            className="bp-icon-btn h-8 w-8 border border-slate-200 bg-white shadow-sm"
            aria-label="Back to dashboard"
            title="Back to dashboard"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-3 gap-y-0.5">
            <h1 className="min-w-0 text-xl font-bold tracking-tight text-slate-900" title={rfp.title || undefined}>
              {rfp.title?.trim() || `RFP #${rfp.id}`}
            </h1>
            <div className="flex flex-wrap items-center gap-x-2 text-xs text-slate-500">
              <span className="font-semibold text-slate-700">RFP #{rfp.id}</span>
              <span className="text-slate-300">•</span>
              <span className="font-semibold text-slate-700">{rfp.platform_type}</span>
              {rfp.created_at && (
                <>
                  <span className="text-slate-300">•</span>
                  <span>{new Date(rfp.created_at).toLocaleDateString()}</span>
                </>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {rfp.input_text && (
            <Button variant="secondary" size="sm" onClick={() => setSourceOpen((prev) => !prev)} aria-expanded={sourceOpen}>
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              {sourceOpen
                ? "Hide RFP text"
                : rfp.platform_type === "Umbrex"
                  ? "RFP text & DC notes"
                  : "View RFP text"}
            </Button>
          )}
        </div>
      </div>

      {sourceOpen && rfp.input_text && (
        <Card className="p-0 animate-slide-up">
          <div className="bp-card-header">
            <div>
              <p className="bp-eyebrow">Source document</p>
              <p className="text-sm text-slate-500">{rfp.input_text.length.toLocaleString()} characters</p>
            </div>
            <button type="button" onClick={() => setSourceOpen(false)} className="bp-icon-btn" aria-label="Close RFP text">
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <pre className="max-h-80 overflow-auto whitespace-pre-wrap break-words px-5 py-4 font-sans text-sm leading-relaxed text-slate-700">
            {rfp.input_text}
          </pre>
          {rfp.platform_type === "Umbrex" && (
            <div className="border-t px-5 py-4" style={{ borderColor: "var(--line-soft)" }}>
              <div className="mb-2 flex items-center justify-between gap-3">
                <div>
                  <p className="bp-eyebrow">DC notes</p>
                  <p className="text-xs text-slate-500">
                    Which background and past work to lead with. Used by the project selector and the proposal.
                  </p>
                </div>
                {!notesEditing && (
                  <Button
                    variant="secondary"
                    size="sm"
                    onClick={() => {
                      setNotesDraft(rfp.bidder_notes || "");
                      setNotesEditing(true);
                    }}
                  >
                    {rfp.bidder_notes ? "Edit notes" : "Add notes"}
                  </Button>
                )}
              </div>
              {notesEditing ? (
                <div className="space-y-2">
                  <textarea
                    aria-label="DC notes"
                    value={notesDraft}
                    onChange={(event) => setNotesDraft(event.target.value.slice(0, 4000))}
                    disabled={savingNotes}
                    className="bp-textarea h-24 w-full text-sm"
                  />
                  <div className="flex justify-end gap-2">
                    <Button variant="secondary" size="sm" onClick={() => setNotesEditing(false)} disabled={savingNotes}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={() => void handleSaveNotes()} disabled={savingNotes}>
                      {savingNotes ? "Saving..." : "Save notes"}
                    </Button>
                  </div>
                </div>
              ) : rfp.bidder_notes ? (
                <>
                  <p className="whitespace-pre-wrap text-sm text-slate-700">{rfp.bidder_notes}</p>
                  {stepsBehindNotes.length > 0 && (
                    <p className="mt-2 text-xs text-amber-700" data-testid="notes-stale-steps">
                      {`Step ${stepsBehindNotes.join(", ")} ran before these notes were saved and does not use them. Re-run or give feedback on a step to apply them.`}
                    </p>
                  )}
                </>
              ) : (
                <p className="text-sm text-slate-500">No notes for this bid.</p>
              )}
            </div>
          )}
        </Card>
      )}

      {error && <div className="bp-alert-danger" role="alert"><svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><span>{error}</span></div>}
      {runError && <div className="bp-alert-danger" role="alert"><svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><span>{runError}</span></div>}
      {reflectionError && <div className="bp-alert-danger" role="alert"><svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><span>{reflectionError}</span></div>}
      {reflectionMessage && <div className="bp-alert-success" role="status"><svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><span>{reflectionMessage}</span></div>}
      {feedbackStatusMessage && (
        <div className="bp-alert-info" role="status">
          <svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
          <span>{feedbackStatusMessage}</span>
        </div>
      )}
      {step5RunningAfterStep4Approval && (
        <div className="bp-alert-accent" role="status">
          <svg className="mt-0.5 h-4 w-4 flex-shrink-0 animate-spin-slow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" d="M12 3a9 9 0 1 0 9 9" strokeOpacity={0.3} /><path strokeLinecap="round" d="M12 3a9 9 0 0 1 9 9" /></svg>
          <span>Step {finalStep} is running in the background. You can review other steps while the final draft is generated.</span>
        </div>
      )}
      {feedbackError && <div className="bp-alert-danger" role="alert"><svg className="mt-0.5 h-4 w-4 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg><span>{feedbackError}</span></div>}
      {confirmRemoveProject && (
        <ConfirmModal
          title="Remove this project from the pool?"
          message={`"${confirmRemoveProject.title}" was picked by the agent. It will not be used in the final step. You can add it back by pasting it as a manual project or by re-running this step.`}
          confirmLabel="Remove project"
          cancelLabel="Keep it"
          variant="danger"
          onConfirm={() => {
            const target = confirmRemoveProject;
            setConfirmRemoveProject(null);
            void handleRemovePoolProject(target.id);
          }}
          onCancel={() => setConfirmRemoveProject(null)}
        />
      )}
      {step4AcceptDialogOpen && (
        <div className="bp-overlay">
          <div className="bp-modal max-w-md p-6" role="dialog" aria-modal="true">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 text-primary-600">
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-900">Add selected experts to the final pitch?</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              You have {step4SelectedExpertIds.length} expert{step4SelectedExpertIds.length === 1 ? "" : "s"} selected.
              Add them to route Step {finalStep} into team mode, or continue without experts for the standard version.
            </p>
            <div className="mt-6 flex flex-wrap justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => handleConfirmStep4Experts(false)}
                disabled={acceptingStepNumber === projectStep}
              >
                Continue Without Experts
              </Button>
              <Button
                type="button"
                onClick={() => handleConfirmStep4Experts(true)}
                disabled={acceptingStepNumber === projectStep || step4SelectedExpertIds.length === 0}
              >
                Add Experts to Final Pitch
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="relative">
        <div className="space-y-3">
          <StepNavigator
            steps={rfp.pipeline_steps.map((s) => ({
              step: s.step,
              name: s.name,
              state: getStepState(s.step),
              outputVersion: (stepVersionByStep.get(s.step) || emptyStepVersionStats).outputVersion,
              feedbackCount: (stepVersionByStep.get(s.step) || emptyStepVersionStats).feedbackCount,
              feedbackPendingRerun: (stepVersionByStep.get(s.step) || emptyStepVersionStats).feedbackPendingRerun,
              accepted: Boolean(rfp.timeline_by_step?.[String(s.step)]?.accepted_output_run_id),
              feedbackSubmitting: feedbackSubmittingStep === s.step,
            }))}
            currentStep={currentStep}
            onSelect={setCurrentStep}
            branchLabel={catalantBranchLabel}
          />

          <Card className="overflow-hidden p-0">
        <div className="flex flex-col gap-3 border-l-4 border-primary-500 bg-gradient-to-r from-primary-50/70 to-white px-4 py-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="min-w-0">
            <div className="flex flex-wrap items-baseline gap-x-2">
              <h3 className="text-sm font-semibold text-slate-900">Next Action</h3>
              <p className="text-sm font-medium text-primary-700">
                Step {currentStep} - {currentPipelineStep?.name || "Unknown"}
              </p>
            </div>
            <p className="mt-0.5 max-w-3xl text-xs leading-relaxed text-slate-600">{getStepGuidance()}</p>
          </div>
          <div className="flex flex-shrink-0 items-center gap-2">
            <button
              type="button"
              onClick={() => setAdvancedOpen((prev) => !prev)}
              aria-expanded={advancedOpen}
              className="inline-flex h-8 items-center gap-1 rounded-lg px-2 text-xs font-medium text-slate-600 transition-colors hover:bg-white hover:text-slate-900"
            >
              <svg className={`h-3.5 w-3.5 transition-transform ${advancedOpen ? "rotate-90" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              {advancedOpen ? "Hide Power Tools" : "Show Power Tools"}
            </button>
            <Button onClick={primaryAction.onClick} disabled={primaryAction.disabled}>
              {primaryAction.label}
            </Button>
          </div>
        </div>

        <div>
          {advancedOpen && (
            <div className="flex flex-wrap gap-2 border-t border-slate-100 px-4 py-2.5 animate-slide-up">
              {rerunRequiredFromStep !== null && (
                <Button
                  size="sm"
                  variant="secondary"
                  onClick={() => handleRerunFrom(rerunRequiredFromStep)}
                  disabled={rerunningFromStep === rerunRequiredFromStep}
                >
                  {rerunningFromStep === rerunRequiredFromStep
                    ? "Resetting..."
                    : `Reset and Rerun from Step ${rerunRequiredFromStep}`}
                </Button>
              )}
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleRunStep(currentStep)}
                disabled={currentStepState === "blocked" || currentStepState === "running" || currentStepBusy}
              >
                {currentStepBusy ? "Running..." : `Re-run Step ${currentStep}`}
              </Button>
              <Button
                size="sm"
                variant="secondary"
                onClick={() => handleAcceptStep(currentStep)}
                disabled={acceptingStepNumber === currentStep || !hasPendingApproval}
              >
                {acceptingStepNumber === currentStep ? "Accepting..." : `Approve Step ${currentStep}`}
              </Button>
            </div>
          )}
        </div>

      </Card>

      <Card className="px-5 py-4">
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2.5">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-2">
            <h3 className="text-base font-semibold text-slate-900">Step {currentStep} Result</h3>
            <p className="truncate text-xs text-slate-500">
              {currentOutput ? `${currentOutput.tab_name}` : "No output yet"}
            </p>
          </div>
          {devMode && (
            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">
              {currentStepVersion.outputVersion > 0
                ? `Visible version: v${currentStepVersion.outputVersion}`
                : "Visible version: none"}
            </span>
          )}
        </div>
        {/* The pool writes a version per edit, reorder or removal, so its history is
            noise rather than something to pick from. */}
        {currentStep !== projectStep && currentStepVersionEntries.length > 1 && (
          <div className="mb-4 rounded-xl border border-slate-200 bg-slate-50/60" data-testid="step-version-history">
            <button
              type="button"
              onClick={() => setVersionsOpen((prev) => !prev)}
              className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left"
              aria-expanded={versionsOpen}
            >
              <span className="text-sm font-semibold text-slate-900">
                Version history ({currentStepVersionEntries.length})
                {acceptedVersionNumber ? ` - approved v${acceptedVersionNumber}` : ""}
              </span>
              <span className="rounded-lg border border-slate-200 bg-white px-2.5 py-1 text-xs font-medium text-slate-600">
                {versionsOpen ? "Collapse" : "Expand"}
              </span>
            </button>
            {versionsOpen && (
              <div className="space-y-2 px-3 pb-3">
                <p className="text-xs text-slate-500">
                  Approving an older version makes it the one the pipeline uses. Restoring writes it back as a new
                  version and leaves the history intact; downstream steps are not rerun either way.
                </p>
                {[...currentStepVersionEntries].reverse().map((entry) => {
                  const isLatest = entry.runId === latestRunId;
                  const isApproved = entry.runId === acceptedRunId;
                  const busy = restoringRunId === entry.runId || acceptingStepNumber === currentStep;
                  return (
                    <div
                      key={entry.runId}
                      className="flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2"
                      data-testid={`step-version-row-${entry.version}`}
                    >
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-sm font-semibold text-slate-900">v{entry.version}</span>
                          {isLatest && (
                            <span className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">
                              Latest
                            </span>
                          )}
                          {isApproved && (
                            <span className="rounded-full border border-green-200 bg-green-50 px-2 py-0.5 text-[11px] font-medium text-green-700">
                              Approved
                            </span>
                          )}
                          {entry.status && entry.status.toLowerCase() !== "final" && (
                            <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
                              {entry.status}
                            </span>
                          )}
                        </div>
                        <p className="truncate text-xs text-slate-500">
                          {versionOriginLabel(entry.inputStatus)} - {new Date(entry.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="flex flex-wrap items-center gap-2">
                        {!isLatest && entry.status.toLowerCase() === "final" && (
                          <button
                            type="button"
                            onClick={() => void handleRestoreVersion(entry.runId)}
                            disabled={busy}
                            className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                          >
                            {restoringRunId === entry.runId ? "Restoring..." : "Restore as new version"}
                          </button>
                        )}
                        {!isApproved && entry.status.toLowerCase() === "final" && (
                          <button
                            type="button"
                            onClick={() => handleAcceptStep(currentStep, entry.runId)}
                            disabled={busy}
                            className="rounded-lg border border-green-200 bg-green-50 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-100 disabled:opacity-50"
                          >
                            Approve this version
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
        {currentStepVersion.feedbackPendingRerun && (
          <div className="bp-alert-warning mb-4">
            New feedback was submitted after this output version. The latest output may still be rerunning.
          </div>
        )}
        {currentOutput?.run_status && String(currentOutput.run_status).toLowerCase() !== "final" && (
          <div className="bp-alert-warning mb-4 block">
            This step is paused with status <span className="font-semibold">{currentOutput.run_status}</span>.{" "}
            {typeof currentOutput.output.status_message === "string"
              ? currentOutput.output.status_message
              : "Provide the needed human input before continuing."}
          </div>
        )}
        {renderStructuredOutput(currentOutputForDisplay, {
          enabled: isStep5PitchOutput,
          persistedParagraphIds,
          draftParagraphIds,
          hoveredParagraphId: hoveredSentenceId,
          paragraphsBySectionKey: step5ParagraphsBySectionKey,
          onParagraphHover: (paragraphId: string) => setHoveredSentenceId(paragraphId),
          onParagraphLeave: (paragraphId: string) =>
            setHoveredSentenceId((prev) => (prev === paragraphId ? null : prev)),
          onParagraphClick: handleStep5SentenceClick,
          disabled: submittingFeedback,
        }, currentStep === projectStep ? {
          selectedExpertIds: step4SelectedExpertIds,
          searchText: step4ExpertSearchText,
          disabled: acceptingStepNumber === projectStep,
          onSearchTextChange: setStep4ExpertSearchText,
          onResetExperts: () => {
            setStep4SelectedExpertIds(step4AgentChoiceExpertIds);
            setStep4ExpertSearchText("");
          },
          onToggleExpert: (expertId: string) =>
            setStep4SelectedExpertIds((prev) =>
              prev.includes(expertId) ? prev.filter((item) => item !== expertId) : [...prev, expertId]
            ),
          draggingExpertId,
          dragOverExpertId,
          onDragStartExpert: (expertId: string) => setDraggingExpertId(expertId),
          onDragOverExpert: (expertId: string) => setDragOverExpertId(expertId),
          onDragEndExpert: () => {
            setDraggingExpertId(null);
            setDragOverExpertId(null);
          },
          onMoveExpert: (expertId: string, direction: -1 | 1) =>
            setStep4SelectedExpertIds((prev) => moveInList(prev, expertId, direction)),
          onReorderExperts: (sourceId: string, targetId: string) =>
            setStep4SelectedExpertIds((prev) => reorderList(prev, sourceId, targetId)),
        } : undefined, manualProjectOptions, pitchEditOptions, {
          hasBidderNotes: Boolean(rfp?.bidder_notes?.trim()),
        })}
        {devMode && typeof currentOutput?.output?.raw_output_text === "string" && currentOutput.output.raw_output_text.trim() && (
          <details className="group mt-4 rounded-xl border border-slate-200 bg-slate-50/50">
            <summary className="flex cursor-pointer list-none items-center gap-2 px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-900">
              <svg className="h-4 w-4 transition-transform group-open:rotate-90" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
              </svg>
              Raw Output (Debug)
            </summary>
            <pre className="bp-code mx-4 mb-4 max-h-[28rem] overflow-auto bg-white">
              {currentOutput.output.raw_output_text}
            </pre>
          </details>
        )}
      </Card>

      <Card className="px-5 py-4">
        {isStep5PitchOutput ? (
          <div className="space-y-3">
            <label className="block text-sm font-semibold text-slate-800">
              Step {finalStep} Targeted Feedback
            </label>
            {submittingFeedback && (
              <div className="bp-alert-info">
                Rerunning Step {finalStep} with your annotations...
              </div>
            )}
            <p className="text-xs text-slate-500">
              Hover and click a paragraph in the Step {finalStep} Result section above to create an annotation.
            </p>
            <div className="rounded-xl border border-slate-200 bg-white p-4">
              <div className="mb-3 flex items-center justify-between">
                <p className="bp-eyebrow">Saved Annotations</p>
                <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium text-slate-600">{step5Annotations.length} total</span>
              </div>
              {step5Annotations.length === 0 ? (
                <p className="rounded-lg border border-dashed border-slate-200 px-3 py-4 text-center text-sm text-slate-500">No annotations saved yet.</p>
              ) : (
                <div className="space-y-2">
                  {step5Annotations.map((annotation, index) => (
                    <div key={annotation.annotation_id} className="rounded-lg border border-amber-200/70 bg-amber-50/40 p-3">
                      <p className="text-xs font-semibold text-slate-700">
                        #{index + 1} {annotation.section_label} (Paragraph {annotation.start_sentence_index})
                      </p>
                      <p className="mt-1 text-xs text-slate-600">{annotation.excerpt_text}</p>
                      <p className="mt-1 text-sm text-slate-900">{annotation.feedback_text}</p>
                      <div className="mt-2 flex gap-2">
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleEditAnnotation(annotation.annotation_id)}
                          disabled={submittingFeedback}
                        >
                          Edit
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          onClick={() => handleDeleteAnnotation(annotation.annotation_id)}
                          disabled={submittingFeedback}
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="mt-3 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Send all saved annotations together to rerun Step {finalStep}.
              </p>
              <Button
                size="sm"
                variant="success"
                onClick={handleSubmitFeedback}
                disabled={submittingFeedback || !annotationFeedbackReady}
              >
                {submittingFeedback ? `Rerunning Step ${finalStep}...` : "Send Feedback"}
              </Button>
            </div>
            {draftRange && (
              <div
                className="bp-overlay"
                onClick={(e) => {
                  if (e.target !== e.currentTarget || popupBusy) return;
                  handleDismissDraftAnnotation();
                }}
              >
                <div
                  className="bp-modal max-h-[calc(100vh-2rem)] w-[calc(100vw-2rem)] max-w-2xl overflow-y-auto p-6"
                  role="dialog"
                  aria-modal="true"
                  aria-labelledby="annotation-modal-title"
                  onKeyDown={(e) => {
                    if (e.key === "Escape" && !popupBusy) {
                      e.preventDefault();
                      handleDismissDraftAnnotation();
                    } else if (e.key === "Enter" && (e.ctrlKey || e.metaKey) && draftAnnotationText.trim()) {
                      e.preventDefault();
                      handleSaveDraftAnnotation();
                    }
                  }}
                >
                  <h3 id="annotation-modal-title" className="flex items-center gap-2 text-lg font-semibold text-slate-900">
                    {editingAnnotationId ? "Edit Annotation" : "New Annotation"}
                    {paragraphDrafts[draftRange.excerpt_text] && (
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">Draft</span>
                    )}
                  </h3>
                  <p className="mt-1 text-sm text-slate-600">
                    {draftRange.section_label} (Paragraph {draftRange.start_sentence_index})
                  </p>
                  <p className="mt-4 max-h-60 overflow-auto rounded-lg border-l-4 border-amber-400 bg-amber-50/60 p-4 text-sm leading-relaxed text-slate-700 whitespace-pre-wrap">
                    {draftRange.excerpt_text}
                  </p>
                  <label htmlFor="annotation-modal-text" className="mt-5 block text-sm font-medium text-slate-800">
                    How should this change?
                  </label>
                  <textarea
                    id="annotation-modal-text"
                    value={draftAnnotationText}
                    onChange={(e) => setDraftAnnotationText(e.target.value)}
                    placeholder="Write what should change in this paragraph..."
                    className="bp-textarea mt-2 h-32"
                    autoFocus
                    disabled={popupBusy}
                  />
                  {canRewriteParagraph && (
                    <div className="mt-3">
                      <Button
                        size="sm"
                        variant="secondary"
                        onClick={() => void handleRunParagraphRewrite()}
                        disabled={popupBusy || !draftAnnotationText.trim()}
                      >
                        {rewriteBusy ? "Rewriting..." : rewriteVersions.length > 0 ? "Try again" : "Run on this paragraph"}
                      </Button>
                    </div>
                  )}
                  {rewriteError && <div className="bp-alert-danger mt-3">{rewriteError}</div>}
                  {rewriteVersions.length > 0 && (
                    <div className="mt-4 rounded-xl border border-slate-200 p-3" data-testid="rewrite-versions">
                      <div className="flex flex-wrap gap-1.5" role="tablist" aria-label="Paragraph versions">
                        {["Original", ...rewriteVersions.map((_, i) => `Version ${i + 1}`)].map((label, i) => {
                          const index = i - 1;
                          const active = (selectedRewrite ?? -1) === index;
                          return (
                            <button
                              key={label}
                              type="button"
                              role="tab"
                              aria-selected={active}
                              onClick={() => setSelectedRewrite(index < 0 ? null : index)}
                              className={`rounded-lg px-2.5 py-1 text-xs font-medium transition-colors ${
                                active ? "bg-primary-600 text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                              }`}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                      <p className="mt-3 max-h-60 overflow-auto whitespace-pre-wrap rounded-lg bg-emerald-50/60 p-3 text-sm leading-relaxed text-slate-800">
                        {selectedRewrite === null ? draftRange.excerpt_text : rewriteVersions[selectedRewrite]}
                      </p>
                      <div className="mt-2 flex items-center justify-between gap-3">
                        <p className="text-xs text-slate-500">
                          {selectedRewrite === null
                            ? "Pick a version to use it in the proposal."
                            : `${rewriteVersions[selectedRewrite].length} characters${
                                rewritePart?.limit ? ` · section limit ${rewritePart.limit}` : ""
                              }`}
                        </p>
                        <Button
                          size="sm"
                          variant="success"
                          onClick={() => void handleApplyParagraphRewrite()}
                          disabled={popupBusy || selectedRewrite === null}
                        >
                          {applyingRewrite ? "Saving..." : "Use this version"}
                        </Button>
                      </div>
                    </div>
                  )}
                  <div className="mt-5 flex items-center justify-between gap-3">
                    <p className="text-xs text-slate-500">Ctrl+Enter to save · click outside keeps a draft</p>
                    <div className="flex gap-2">
                      <Button variant="secondary" onClick={handleCancelDraftAnnotation} disabled={popupBusy}>
                        Cancel
                      </Button>
                      <Button onClick={handleSaveDraftAnnotation} disabled={popupBusy || !draftAnnotationText.trim()}>
                        Save
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <>
            <label className="mb-2 block text-sm font-semibold text-slate-800">
              Feedback for Step {currentStep}
            </label>
            <p className="mb-2 text-xs text-slate-500">
              Say what to change and why - add or drop content, reorder it, fix a fact, or change the tone. Plain
              sentences work; the step reruns with your notes.
            </p>
            <textarea
              value={feedbackText}
              onChange={(e) => setFeedbackText(e.target.value)}
              aria-label={`Feedback for Step ${currentStep}`}
              placeholder={feedbackPlaceholder}
              className="bp-textarea h-20"
              disabled={submittingFeedback}
            />
            <div className="mt-2 flex items-center justify-between gap-3">
              <p className="text-xs text-slate-500">
                Submit feedback to update this step and invalidate downstream outputs if needed.
              </p>
              <Button
                size="sm"
                variant="success"
                onClick={handleSubmitFeedback}
                disabled={submittingFeedback || !feedbackText.trim()}
              >
                {submittingFeedback ? "Submitting..." : "Submit Feedback"}
              </Button>
            </div>
          </>
        )}
      </Card>

      {devMode && (
      <Card className="px-5 py-3">
        <button
          type="button"
          onClick={() => setTimelineOpen((prev) => !prev)}
          className="flex w-full items-center justify-between gap-3 text-left"
          aria-expanded={timelineOpen}
        >
          <div className="flex items-center gap-2">
            <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="text-sm font-semibold text-slate-900">Interaction Timeline - Step {currentStep}</h3>
          </div>
          <span className="rounded-lg border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50">{timelineOpen ? "Collapse" : "Expand"}</span>
        </button>

        {timelineOpen ? (
          <div className="mt-3">
            {timelineForStep.accepted_output_run_id && (
              <div className="bp-alert-success mb-4">
                This step has an approved version.
              </div>
            )}

            {timelineForStep.events.length === 0 ? (
              <p className="text-sm text-slate-600">No timeline events yet for this step.</p>
            ) : (
              <div className="relative space-y-3 pl-5 before:absolute before:bottom-2 before:left-[7px] before:top-2 before:w-px before:bg-slate-200">
                {timelineForStep.events.map((event, index) => {
                  const eventStyle = {
                    agent_input: "border-blue-200 bg-blue-50",
                    agent_output: "border-indigo-200 bg-indigo-50",
                    human_feedback: "border-amber-200 bg-amber-50",
                    accepted_output: "border-green-200 bg-green-50",
                    memory_retrieval: "border-cyan-200 bg-cyan-50",
                    tool_call: "border-slate-200 bg-slate-50",
                  }[event.event_type];
                  const eventKey = `${event.timestamp}-${event.event_type}-${index}`;
                  const expanded = Boolean(expandedTimelineEvents[eventKey]);
                  const rawExpanded = Boolean(expandedTimelineRaw[eventKey]);
                  const isDiagnosticEvent = event.event_type === "memory_retrieval" || event.event_type === "tool_call";

                  return (
                    <div key={eventKey} className={`relative rounded-xl border p-3.5 before:absolute before:-left-[17px] before:top-5 before:h-2.5 before:w-2.5 before:rounded-full before:border-2 before:border-white before:bg-slate-400 before:ring-1 before:ring-slate-200 ${eventStyle}`}>
                      <button
                        type="button"
                        onClick={() => toggleTimelineEvent(eventKey)}
                        className="flex w-full items-center justify-between text-left"
                      >
                        <div>
                          <p className="text-xs font-semibold uppercase tracking-wide text-slate-700">{eventTypeLabel[event.event_type]}</p>
                          <p className="text-sm font-semibold text-slate-900">{event.title}</p>
                          <p className="text-xs text-slate-600">
                            {event.actor} - {new Date(event.timestamp).toLocaleString()}
                          </p>
                        </div>
                        <span className="flex-shrink-0 rounded-lg bg-white/70 px-2 py-1 text-xs font-medium text-slate-600">{expanded ? "Hide details" : "Show details"}</span>
                      </button>

                      {expanded && (
                        isDiagnosticEvent ? (
                          <div>
                            {renderTimelineDiagnosticContent(event)}
                            {event.raw_content !== undefined && (
                              <div className="mt-3">
                                <button
                                  type="button"
                                  onClick={() => toggleTimelineRaw(eventKey)}
                                  className="text-xs font-semibold text-slate-700"
                                >
                                  {rawExpanded ? "Hide raw JSON" : "Show raw JSON"}
                                </button>
                                {rawExpanded && (
                                  <pre className="bp-code mt-2 bg-white">
                                    {formatContent(event.raw_content)}
                                  </pre>
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <pre className="bp-code mt-3 bg-white">
                            {formatContent(event.content)}
                          </pre>
                        )
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <p className="mt-1 text-xs text-slate-400">Timeline is collapsed.</p>
        )}
      </Card>
      )}

        </div>
      </div>
    </div>
  );
}




